// src/components/BuildingDesigner/ElementForm.jsx
//
// Stage 2 form for rectangular rooms.
// Fields: name, label, x, y, width, height + doors/windows/open doors.
// Click-mode toggle: "corner" (single-click SW) vs "twoCorner"
//                    (click SW then NE, w/h auto).

import React from "react";
import styled from "styled-components";
import { polygonIsClockwise, edgeSwing } from "./geoUtils";

const Row = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
`;
const Label = styled.label`
  display: block;
  font-size: 12px;
  color: #555;
  margin-bottom: 2px;
`;
const Input = styled.input`
  width: 100%;
  padding: 5px 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  box-sizing: border-box;
`;
const Section = styled.div`
  margin: 12px 0;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fafafa;
`;
const H4 = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #333;
`;
const Chip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(p) =>
    p.$type === "door"
      ? "#c0392b"
      : p.$type === "window"
        ? "#2e86de"
        : "#7f8c8d"};
  color: white;
  font-size: 11px;
  margin-right: 4px;
`;
const SmallBtn = styled.button`
  padding: 4px 8px;
  font-size: 11px;
  background: ${(p) => (p.$primary ? "teal" : "#f4f4f4")};
  color: ${(p) => (p.$primary ? "white" : "#333")};
  border: 1px solid ${(p) => (p.$primary ? "teal" : "#ccc")};
  border-radius: 4px;
  cursor: pointer;
`;
const DeleteBtn = styled.button`
  padding: 2px 6px;
  font-size: 11px;
  background: #fff;
  color: #c0392b;
  border: 1px solid #f2c1bc;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 6px;
  &:hover {
    background: #fdecea;
  }
`;
const AddBtn = styled.button`
  width: 100%;
  padding: 8px 10px;
  background: teal;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 6px;
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

// ── Defaults for a fresh in-progress room ──────────────────────────────
export function makeEmptyDrawing(type) {
  if (type === "CUSTOM") {
    return {
      type,
      isCustom: true,
      subtype: "HALL", // architectural type for the polygon (edit in form)
      name: "",
      label: "",
      points: [], // list of [x, y] in world metres
      closed: true, // decided when user hits Enter or "Keep open"
      polygonPhase: "points", // "points" | "openings"
      doors: [],
      windows: [],
      open_doors: [],
    };
  }
  if (type === "SHAFT") {
    return {
      type,
      isShaft: true,
      name: "",
      label: "",
      x: 0,
      y: 0,
      // shape params — same defaults as the CLI
      steps: 22,
      step_width: 1.5,
      width_dir: "x", // "x" or "y"
      depth: 0.3,
      separation: 0.3,
      flat: 1.5,
      rotation: "clockwise", // UI-only — used to compute up_dir
      // Openings default is_main=true (main doors — swing OUTSIDE the shaft).
      doors: [],
      windows: [],
      open_doors: [],
      elevators: [], // Stage 3c-2 will populate this
    };
  }
  if (type === "STAIRS") {
    // Simple outdoor stairs — one straight flight (not U-shaped like SHAFT).
    // Doors are MAIN doors by default. No windows in the Python schema.
    return {
      type,
      isStairs: true,
      name: "",
      label: "",
      x: 0,
      y: 0,
      steps: 5,
      width: 1.2,
      width_dir: "x",
      depth: 0.3,
      rotation: "clockwise", // UI-only — mapped to up_dir like SHAFT
      doors: [],
      open_doors: [],
    };
  }
  if (type === "BEAM") {
    // Standalone wall — two endpoints, no openings.
    // Python renders as double-line 0.2m thick with auto-hidden caps.
    return {
      type,
      isBeam: true,
      name: "",
      label: "",
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 0,
      // UI-only phase tracker: "p1" waiting for first click,
      // "p2" waiting for second click, "done" both placed.
      wallPhase: "p1",
    };
  }
  if (type === "COLUMN") {
    // Structural column — one click places it centered at the point.
    // Drawn as a red rectangle (width × thickness) on the A-COLUMN layer.
    return {
      type,
      isColumn: true,
      name: "",
      label: "",
      x: 0,
      y: 0,
      thickness: 0.2,      // one of 0.2 / 0.25 / 0.3
      width: 0.4,          // one of 0.4 / 0.5 / 0.6 / 0.7 / 0.8 / 0.9 / 1.0
      direction: "x",      // "x" | "y" | "angled"
      angle: 0,            // degrees, used only when direction === "angled"
      // UI-only phase tracker: "p1" waiting for the placement click.
      columnPhase: "p1",
    };
  }
  return {
    type,
    name: "",
    label: "",
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    doors: [],
    windows: [],
    open_doors: [],
  };
}

// Compute the shaft's rectangle dimensions and up_dir from the shape params.
// Matches Python's StairShaft.run_len / .width_total / .block_w / .block_h
// and the CLI's up_dir logic (main.py lines 489-492).
export function shaftDerived(s) {
  const steps = Math.max(2, Math.floor(s.steps ?? 22));
  const stepWidth = Math.max(0.1, Number(s.step_width ?? 1.5));
  const depth = Math.max(0.1, Number(s.depth ?? 0.3));
  const separation = Math.max(0.0, Number(s.separation ?? 0.3));
  const flat = Math.max(0.0, Number(s.flat ?? 1.5));
  const widthDir = s.width_dir === "y" ? "y" : "x";
  const runLen = Math.floor(steps / 2) * depth + 2 * flat;
  const widthTotal = 2 * stepWidth + separation;
  const blockW = widthDir === "x" ? widthTotal : runLen;
  const blockH = widthDir === "x" ? runLen : widthTotal;
  // up_dir: CW → NORTH (if width_dir=x) / WEST (if y). CCW → SOUTH / EAST.
  const rotCw = (s.rotation ?? "clockwise") === "clockwise";
  const upDir =
    widthDir === "x" ? (rotCw ? "north" : "south") : rotCw ? "west" : "east";
  return {
    runLen,
    widthTotal,
    blockW,
    blockH,
    upDir,
    steps,
    stepWidth,
    depth,
    separation,
    flat,
    widthDir,
  };
}

// Compute the STAIRS block dimensions and up_dir from shape params.
// Mirrors Python Stairs.block_w / .block_h.
// Block = width × run, where run = steps × depth.
export function stairsDerived(s) {
  const steps = Math.max(2, Math.floor(s.steps ?? 5));
  const width = Math.max(0.1, Number(s.width ?? 1.2));
  const depth = Math.max(0.3, Number(s.depth ?? 0.3));
  const widthDir = s.width_dir === "y" ? "y" : "x";
  const runLen = steps * depth;
  const blockW = widthDir === "x" ? width : runLen;
  const blockH = widthDir === "x" ? runLen : width;
  // up_dir: CW → NORTH (if width_dir=x) / WEST (if y). CCW → SOUTH / EAST.
  // (Same mapping as SHAFT for a consistent UX.)
  const rotCw = (s.rotation ?? "clockwise") === "clockwise";
  const upDir =
    widthDir === "x" ? (rotCw ? "north" : "south") : rotCw ? "west" : "east";
  return { runLen, blockW, blockH, upDir, steps, width, depth, widthDir };
}

// The visible "which side of the room did the user click" helper.
const wallLabel = (w) => (w || "?").toUpperCase();

export default function ElementForm({
  selectedType,
  drawing,
  setDrawing,
  clickMode,
  setClickMode,
  openingMode,
  setOpeningMode, // null | "door" | "window" | "open_door"
  openingDraft,
  setOpeningDraft, // partial opening being placed
  floor,
  setFloor,
  editIndex, // null = adding; number = editing that idx
  onEditElement, // (idx) => void  (Edit button in list)
  onCancelEdit, // ()   => void  (Cancel edit)
  onCommitElement, // click "Add room to plan" or "Save changes"
  onDeleteElement, // (idx) => void
  inputStep = 0.1,
  setInputStep,
}) {
  if (!selectedType) {
    return (
      <div style={{ fontSize: 13, color: "#666", padding: 10 }}>
        Pick an element type from the right panel to begin.
      </div>
    );
  }

  const isRectType = [
    "BEDROOM",
    "BATHROOM",
    "KITCHEN",
    "LIVING",
    "DINING",
    "HALL",
    "STORAGE",
    "BALCONY",
  ].includes(selectedType);
  const isCustomType = selectedType === "CUSTOM";
  const isShaftType = selectedType === "SHAFT";
  const isStairsType = selectedType === "STAIRS";
  const isBeamType = selectedType === "BEAM";
  const isColumnType = selectedType === "COLUMN";
  if (
    !isRectType &&
    !isCustomType &&
    !isShaftType &&
    !isStairsType &&
    !isBeamType &&
    !isColumnType
  ) {
    return (
      <div style={{ fontSize: 13, color: "#666", padding: 10 }}>
        {selectedType} — coming in Stage 3.
      </div>
    );
  }

  // `drawing` is created by the page's useEffect one render after the type
  // is picked. Show a tiny placeholder on that first render so we don't
  // crash trying to read fields off null.
  if (!drawing) {
    return (
      <div style={{ fontSize: 13, color: "#666", padding: 10 }}>
        Preparing {selectedType} form...
      </div>
    );
  }

  // Dispatch to the CUSTOM polygon form when appropriate.
  if (isCustomType && drawing.isCustom) {
    return (
      <CustomForm
        drawing={drawing}
        setDrawing={setDrawing}
        floor={floor}
        editIndex={editIndex}
        openingMode={openingMode}
        setOpeningMode={setOpeningMode}
        openingDraft={openingDraft}
        setOpeningDraft={setOpeningDraft}
        onCommitElement={onCommitElement}
        onCancelEdit={onCancelEdit}
        inputStep={inputStep}
        setInputStep={setInputStep}
      />
    );
  }

  // Dispatch to the SHAFT form when appropriate.
  if (isShaftType && drawing.isShaft) {
    return (
      <ShaftForm
        drawing={drawing}
        setDrawing={setDrawing}
        floor={floor}
        editIndex={editIndex}
        openingMode={openingMode}
        setOpeningMode={setOpeningMode}
        openingDraft={openingDraft}
        setOpeningDraft={setOpeningDraft}
        onCommitElement={onCommitElement}
        onCancelEdit={onCancelEdit}
      />
    );
  }

  // Dispatch to the STAIRS form when appropriate.
  if (isStairsType && drawing.isStairs) {
    return (
      <StairsForm
        drawing={drawing}
        setDrawing={setDrawing}
        floor={floor}
        editIndex={editIndex}
        openingMode={openingMode}
        setOpeningMode={setOpeningMode}
        openingDraft={openingDraft}
        setOpeningDraft={setOpeningDraft}
        onCommitElement={onCommitElement}
        onCancelEdit={onCancelEdit}
      />
    );
  }

  // Dispatch to the WALL form when appropriate.
  if (isBeamType && drawing.isBeam) {
    return (
      <WallForm
        drawing={drawing}
        setDrawing={setDrawing}
        editIndex={editIndex}
        onCommitElement={onCommitElement}
        onCancelEdit={onCancelEdit}
        inputStep={inputStep}
        setInputStep={setInputStep}
      />
    );
  }

  // Dispatch to the COLUMN form.
  if (isColumnType && drawing.isColumn) {
    return (
      <ColumnForm
        drawing={drawing}
        setDrawing={setDrawing}
        editIndex={editIndex}
        onCommitElement={onCommitElement}
        onCancelEdit={onCancelEdit}
        inputStep={inputStep}
        setInputStep={setInputStep}
      />
    );
  }

  const d = drawing;
  // Bathrooms have relaxed dimensional constraints — they can be as small
  // as 1×1 m, with narrower door and window widths, since they only need
  // to fit a person and basic fixtures. All other room types keep the
  // regular 2×2 m minimum + wider opening ranges.
  const isBathroom = d?.type === "BATHROOM";
  const isBalcony = d?.type === "BALCONY";
  const minSize = isBathroom ? 1 : 2;
  const doorMin = 0.7; // shared floor
  const doorMax = isBathroom ? 0.8 : 2.0;
  const doorDflt = isBathroom ? 0.75 : 1.1;
  const winMin = 0.3; // shared floor
  const winMax = isBathroom ? 0.7 : 1.7;
  const winDflt = isBathroom ? 0.5 : 1.5;
  // Balcony rule: it's a strip, not a square. The SHORTER side ≥ 0.6 m
  // and the LONGER side ≥ 1.0 m. Automatic orientation — user just types
  // width and height in either order.
  const canAddBalcony =
    d &&
    d.name &&
    Math.min(d.width, d.height) >= 0.6 &&
    Math.max(d.width, d.height) >= 1.0;
  const canAddRect = d && d.name && d.width >= minSize && d.height >= minSize;
  const canAdd = isBalcony ? canAddBalcony : canAddRect;
  const upd = (patch) => setDrawing({ ...d, ...patch });

  // ── Opening add helpers ──────────────────────────────────────────────
  const startOpening = (kind) => {
    setOpeningMode(kind);
    // Reset per-opening draft. For open doors we need begin+end so use
    // a "step" field to know which click we're expecting.
    setOpeningDraft({ kind, step: 1 });
  };
  const cancelOpening = () => {
    setOpeningMode(null);
    setOpeningDraft(null);
  };

  const commitDoorOrWindow = () => {
    if (!openingDraft || !openingDraft.wall || !openingDraft.width) return;
    if (openingDraft.kind === "door") {
      // The Python schema expects `position` to be the HINGE point along
      // the wall (measured from wall start). Python then extends the door
      // gap in the correct direction based on hinge_side:
      //   hinge_side="end"   → gap is [position - width, position]
      //   hinge_side="start" → gap is [position, position + width]
      // Since the user clicked the hinge, position = openingDraft.pos.
      const wallLen = ["south", "north"].includes(openingDraft.wall)
        ? d.width
        : d.height;
      let pos = openingDraft.pos;
      // Clamp so the door gap fits inside the wall.
      if (openingDraft.hinge_side === "end") {
        if (pos < openingDraft.width) pos = openingDraft.width;
        if (pos > wallLen) pos = wallLen;
      } else {
        // "start"
        if (pos < 0) pos = 0;
        if (pos + openingDraft.width > wallLen)
          pos = wallLen - openingDraft.width;
      }
      const door = {
        wall: openingDraft.wall,
        position: pos,
        width: openingDraft.width,
        swing: openingDraft.swing || "right",
        hinge_side: openingDraft.hinge_side || "start",
        is_main: !!openingDraft.is_main,
        connects_to: "",
      };
      upd({ doors: [...d.doors, door] });
    } else if (openingDraft.kind === "window") {
      // Window position is CENTRE-based in the Python schema. Also clamp
      // so the window fits: pos_center ∈ [width/2, wallLen − width/2].
      const wallLen = ["south", "north"].includes(openingDraft.wall)
        ? d.width
        : d.height;
      let pos = openingDraft.pos;
      const half = openingDraft.width / 2;
      if (pos < half) pos = half;
      if (pos > wallLen - half) pos = wallLen - half;
      const win = {
        wall: openingDraft.wall,
        position: pos,
        width: openingDraft.width,
      };
      upd({ windows: [...d.windows, win] });
    }
    cancelOpening();
  };

  const commitOpenDoor = () => {
    if (
      !openingDraft ||
      !openingDraft.wall ||
      openingDraft.pos1 == null ||
      openingDraft.pos2 == null
    )
      return;
    const pos = Math.min(openingDraft.pos1, openingDraft.pos2);
    const wid = Math.abs(openingDraft.pos2 - openingDraft.pos1);
    if (wid < 0.05) {
      alert("Open door width is too small.");
      return;
    }
    upd({
      open_doors: [
        ...d.open_doors,
        {
          wall: openingDraft.wall,
          position: pos,
          width: wid,
        },
      ],
    });
    cancelOpening();
  };

  return (
    <>
      <div
        style={{
          padding: 8,
          background: editIndex !== null ? "#fff8dc" : "#e0f2f1",
          border:
            editIndex !== null ? "1px solid #f0c040" : "1px solid #b2dfdb",
          borderRadius: 6,
          marginBottom: 12,
          fontSize: 13,
        }}
      >
        {editIndex !== null ? (
          <>
            Editing: <strong>{d.label || d.name}</strong> ({selectedType})
          </>
        ) : (
          <>
            Drawing: <strong>{selectedType}</strong>
          </>
        )}
      </div>

      <Row>
        <div style={{ flex: 1 }}>
          <Label>Name</Label>
          <Input
            value={d.name}
            onChange={(e) => upd({ name: e.target.value })}
            placeholder="e.g. bedroom_1"
          />
        </div>
        <div style={{ flex: 1 }}>
          <Label>Label (on plan)</Label>
          <Input
            value={d.label}
            onChange={(e) => upd({ label: e.target.value })}
            placeholder="e.g. BEDROOM 1"
          />
        </div>
      </Row>

      <Section>
        <H4>Click mode</H4>
        <Row style={{ marginBottom: 0 }}>
          <label style={{ flex: 1, fontSize: 12 }}>
            <input
              type="radio"
              checked={clickMode === "corner"}
              onChange={() => setClickMode("corner")}
            />{" "}
            Single click = SW corner
          </label>
          <label style={{ flex: 1, fontSize: 12 }}>
            <input
              type="radio"
              checked={clickMode === "twoCorner"}
              onChange={() => setClickMode("twoCorner")}
            />{" "}
            Two clicks: SW → NE
          </label>
        </Row>
      </Section>

      <Row>
        <div style={{ flex: 1 }}>
          <Label>x (m)</Label>
          <Input
            type="number"
            step={inputStep}
            value={d.x}
            onChange={(e) => upd({ x: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Label>y (m)</Label>
          <Input
            type="number"
            step={inputStep}
            value={d.y}
            onChange={(e) => upd({ y: parseFloat(e.target.value) || 0 })}
          />
        </div>
      </Row>
      <Row>
        <div style={{ flex: 1 }}>
          <Label>
            width (m) —{" "}
            {isBalcony
              ? "strip: short ≥ 0.6, long ≥ 1.0"
              : `min ${minSize.toFixed(1)}`}
          </Label>
          <Input
            type="number"
            min={isBalcony ? 0.6 : minSize}
            step={inputStep}
            value={d.width}
            onChange={(e) => {
              let w = parseFloat(e.target.value) || 0;
              const floor = isBalcony ? 0.6 : minSize;
              if (w > 0 && w < floor) w = floor;
              // Rescale openings on south/north walls to preserve their
              // anchor (start / end / center) as the wall length changes.
              const rescaled = rescaleOpenings(d, "width", w);
              upd({ width: w, ...rescaled });
            }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Label>
            height (m) —{" "}
            {isBalcony
              ? "strip: short ≥ 0.6, long ≥ 1.0"
              : `min ${minSize.toFixed(1)}`}
          </Label>
          <Input
            type="number"
            min={isBalcony ? 0.6 : minSize}
            step={inputStep}
            value={d.height}
            onChange={(e) => {
              let h = parseFloat(e.target.value) || 0;
              const floor = isBalcony ? 0.6 : minSize;
              if (h > 0 && h < floor) h = floor;
              const rescaled = rescaleOpenings(d, "height", h);
              upd({ height: h, ...rescaled });
            }}
          />
        </div>
      </Row>

      <Row style={{ marginBottom: 12, alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 12, color: "#555" }}>Step:</span>
        <button
          type="button"
          onClick={() => setInputStep && setInputStep(0.1)}
          style={{
            padding: "3px 10px",
            fontSize: 12,
            background: inputStep === 0.1 ? "#2e8b57" : "#fff",
            color: inputStep === 0.1 ? "white" : "#333",
            border: "1px solid #bfdac9",
            borderRadius: 4,
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          0.1 m
        </button>
        <button
          type="button"
          onClick={() => setInputStep && setInputStep(0.01)}
          style={{
            padding: "3px 10px",
            fontSize: 12,
            background: inputStep === 0.01 ? "#2e8b57" : "#fff",
            color: inputStep === 0.01 ? "white" : "#333",
            border: "1px solid #bfdac9",
            borderRadius: 4,
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          0.01 m
        </button>
      </Row>

      {/* ── Openings section (only if we have a real rect) ── */}
      {d.width > 0 && d.height > 0 && (
        <Section>
          <H4>Openings</H4>

          {/* Existing openings — quick list with delete */}
          {d.doors.map((o, i) => (
            <div key={`d${i}`} style={{ fontSize: 11, marginBottom: 3 }}>
              <Chip $type="door">DOOR</Chip>
              {wallLabel(o.wall)} pos {o.position.toFixed(2)}m w {o.width}m
              <DeleteBtn
                onClick={() =>
                  upd({ doors: d.doors.filter((_, j) => j !== i) })
                }
              >
                ×
              </DeleteBtn>
            </div>
          ))}
          {d.windows.map((o, i) => (
            <div key={`w${i}`} style={{ fontSize: 11, marginBottom: 3 }}>
              <Chip $type="window">WIN</Chip>
              {wallLabel(o.wall)} centre {o.position.toFixed(2)}m w {o.width}m
              <DeleteBtn
                onClick={() =>
                  upd({ windows: d.windows.filter((_, j) => j !== i) })
                }
              >
                ×
              </DeleteBtn>
            </div>
          ))}
          {d.open_doors.map((o, i) => (
            <div key={`o${i}`} style={{ fontSize: 11, marginBottom: 3 }}>
              <Chip $type="open">OPEN</Chip>
              {wallLabel(o.wall)} pos {o.position.toFixed(2)}m w{" "}
              {o.width.toFixed(2)}m
              <DeleteBtn
                onClick={() =>
                  upd({ open_doors: d.open_doors.filter((_, j) => j !== i) })
                }
              >
                ×
              </DeleteBtn>
            </div>
          ))}

          {/* No opening being placed → show add buttons.
              Balconies only get an OPEN DOOR (the passage from the room). */}
          {!openingMode && (
            <Row style={{ marginTop: 8, marginBottom: 0 }}>
              {!isBalcony && (
                <SmallBtn $primary onClick={() => startOpening("door")}>
                  + Door
                </SmallBtn>
              )}
              {!isBalcony && (
                <SmallBtn $primary onClick={() => startOpening("window")}>
                  + Window
                </SmallBtn>
              )}
              <SmallBtn $primary onClick={() => startOpening("open_door")}>
                + Open door
              </SmallBtn>
            </Row>
          )}

          {/* Placing a DOOR or WINDOW: waiting for a wall click */}
          {openingMode && !openingDraft?.wall && (
            <div
              style={{
                marginTop: 8,
                padding: 8,
                background: "#fff3cd",
                border: "1px solid #ffeeba",
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              Click on a wall of the room to place the{" "}
              <strong>{openingMode.replace("_", " ")}</strong>.
              <SmallBtn onClick={cancelOpening} style={{ float: "right" }}>
                Cancel
              </SmallBtn>
            </div>
          )}

          {/* DOOR form after the hinge point is clicked */}
          {openingMode === "door" && openingDraft?.wall && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, marginBottom: 6 }}>
                Wall: <strong>{wallLabel(openingDraft.wall)}</strong>, hinge at{" "}
                {openingDraft.pos.toFixed(2)}m
              </div>
              <Label>
                Width (m) — range {doorMin.toFixed(1)} to {doorMax.toFixed(1)}
              </Label>
              <Input
                type="number"
                min={doorMin}
                max={doorMax}
                step={inputStep}
                value={openingDraft.width || doorDflt}
                onChange={(e) => {
                  let w = parseFloat(e.target.value) || 0;
                  if (w > 0 && w < doorMin) w = doorMin;
                  if (w > doorMax) w = doorMax;
                  setOpeningDraft({ ...openingDraft, width: w });
                }}
              />
              <Label style={{ marginTop: 6 }}>Rotation</Label>
              <Row style={{ marginBottom: 6 }}>
                <label style={{ flex: 1, fontSize: 12 }}>
                  <input
                    type="radio"
                    checked={openingDraft.rotation === "clockwise"}
                    onChange={() => {
                      const swing = swingFromRotation(
                        openingDraft.wall,
                        "clockwise",
                        !!openingDraft.is_main
                      );
                      const hinge = hingeFromRotation(
                        openingDraft.wall,
                        "clockwise",
                        !!openingDraft.is_main
                      );
                      setOpeningDraft({
                        ...openingDraft,
                        rotation: "clockwise",
                        swing,
                        hinge_side: hinge,
                      });
                    }}
                  />{" "}
                  CW
                </label>
                <label style={{ flex: 1, fontSize: 12 }}>
                  <input
                    type="radio"
                    checked={openingDraft.rotation === "counterclockwise"}
                    onChange={() => {
                      const swing = swingFromRotation(
                        openingDraft.wall,
                        "counterclockwise",
                        !!openingDraft.is_main
                      );
                      const hinge = hingeFromRotation(
                        openingDraft.wall,
                        "counterclockwise",
                        !!openingDraft.is_main
                      );
                      setOpeningDraft({
                        ...openingDraft,
                        rotation: "counterclockwise",
                        swing,
                        hinge_side: hinge,
                      });
                    }}
                  />{" "}
                  CCW
                </label>
              </Row>
              <label style={{ fontSize: 12 }}>
                <input
                  type="checkbox"
                  checked={!!openingDraft.is_main}
                  onChange={(e) => {
                    const is_main = e.target.checked;
                    const rot = openingDraft.rotation || "clockwise";
                    setOpeningDraft({
                      ...openingDraft,
                      is_main,
                      swing: swingFromRotation(openingDraft.wall, rot, is_main),
                      hinge_side: hingeFromRotation(
                        openingDraft.wall,
                        rot,
                        is_main
                      ),
                    });
                  }}
                />{" "}
                Main door (opens outside)
              </label>
              <Row style={{ marginTop: 8 }}>
                <SmallBtn
                  $primary
                  onClick={commitDoorOrWindow}
                  disabled={!openingDraft.rotation}
                >
                  Add door
                </SmallBtn>
                <SmallBtn onClick={cancelOpening}>Cancel</SmallBtn>
              </Row>
            </div>
          )}

          {/* WINDOW form after centre click */}
          {openingMode === "window" && openingDraft?.wall && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, marginBottom: 6 }}>
                Wall: <strong>{wallLabel(openingDraft.wall)}</strong>, centre at{" "}
                {openingDraft.pos.toFixed(2)}m
              </div>
              <Label>
                Width (m) — range {winMin.toFixed(1)} to {winMax.toFixed(1)}
              </Label>
              <Input
                type="number"
                min={winMin}
                max={winMax}
                step={inputStep}
                value={openingDraft.width || winDflt}
                onChange={(e) => {
                  let w = parseFloat(e.target.value) || 0;
                  if (w > 0 && w < winMin) w = winMin;
                  if (w > winMax) w = winMax;
                  setOpeningDraft({ ...openingDraft, width: w });
                }}
              />
              <Row style={{ marginTop: 8 }}>
                <SmallBtn $primary onClick={commitDoorOrWindow}>
                  Add window
                </SmallBtn>
                <SmallBtn onClick={cancelOpening}>Cancel</SmallBtn>
              </Row>
            </div>
          )}

          {/* OPEN DOOR: needs two clicks */}
          {openingMode === "open_door" && openingDraft && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              {openingDraft.pos1 == null && (
                <>
                  Click <strong>begin point</strong> on any wall.
                </>
              )}
              {openingDraft.pos1 != null && openingDraft.pos2 == null && (
                <>
                  Begin: {wallLabel(openingDraft.wall)} @{" "}
                  {openingDraft.pos1.toFixed(2)}m. Now click{" "}
                  <strong>end point</strong> on the same wall.
                </>
              )}
              {openingDraft.pos2 != null && (
                <div>
                  {wallLabel(openingDraft.wall)}: {openingDraft.pos1.toFixed(2)}
                  m → {openingDraft.pos2.toFixed(2)}m (width{" "}
                  {Math.abs(openingDraft.pos2 - openingDraft.pos1).toFixed(2)}m)
                  <Row style={{ marginTop: 6 }}>
                    <SmallBtn $primary onClick={commitOpenDoor}>
                      Add open door
                    </SmallBtn>
                    <SmallBtn onClick={cancelOpening}>Cancel</SmallBtn>
                  </Row>
                </div>
              )}
            </div>
          )}
        </Section>
      )}

      {/* Save changes / Add room button — text depends on edit mode */}
      {editIndex !== null ? (
        <>
          <AddBtn
            disabled={!canAdd}
            onClick={onCommitElement}
            style={{ background: canAdd ? "#2e8b57" : "#ccc" }}
          >
            {canAdd
              ? "Save changes"
              : !d.name
                ? "Fill name to save"
                : isBalcony
                  ? "Balcony: short side ≥ 0.6m, long side ≥ 1m"
                  : `Size must be ≥ ${minSize} × ${minSize} m`}
          </AddBtn>
          <button
            onClick={onCancelEdit}
            style={{
              width: "100%",
              marginTop: 6,
              padding: "8px 10px",
              background: "#f4f4f4",
              color: "#333",
              border: "1px solid #ccc",
              borderRadius: 6,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Cancel edit
          </button>
        </>
      ) : (
        <AddBtn disabled={!canAdd} onClick={onCommitElement}>
          {canAdd
            ? "Add room to plan"
            : !d.name
              ? "Fill name to add"
              : isBalcony
                ? "Balcony: short side ≥ 0.6m, long side ≥ 1m"
                : `Size must be ≥ ${minSize} × ${minSize} m`}
        </AddBtn>
      )}

      {/* The "Elements in plan" list has been moved to a stand-alone
          component (ElementsList) rendered by the page below this form,
          so it stays visible even when no element type is selected. */}
    </>
  );
}

// ── Swing / hinge tables (mirror Python _hinge_side_for_wall / _swing_inside) ─
function swingFromRotation(wall, rotation, isMain) {
  // Per Python: swing mapping is UNIFORM for both regular and main doors
  // (CW → right, CCW → left). Only hinge_side inverts for main doors.
  // With the correct hinge_side (from hingeFromRotation), the swing arc
  // automatically lands OUTSIDE the room for main doors.
  const map = { clockwise: "right", counterclockwise: "left" };
  return map[rotation] || "right";
}
function hingeFromRotation(wall, rotation, isMain) {
  // From _hinge_side_for_wall (inside rooms):
  //   SOUTH cw→start / ccw→end
  //   NORTH cw→end   / ccw→start
  //   WEST  cw→end   / ccw→start
  //   EAST  cw→start / ccw→end
  const inside = {
    south: { clockwise: "start", counterclockwise: "end" },
    north: { clockwise: "end", counterclockwise: "start" },
    west: { clockwise: "end", counterclockwise: "start" },
    east: { clockwise: "start", counterclockwise: "end" },
  };
  const s = inside[wall]?.[rotation] || "start";
  if (!isMain) return s;
  // Main door inverses hinge_side
  return s === "start" ? "end" : "start";
}

// ═════════════════════════════════════════════════════════════════════════
//  RESCALE OPENINGS  when a rect room's width or height changes
// ═════════════════════════════════════════════════════════════════════════
//
// For each opening on the affected walls (south/north when width changes,
// west/east when height changes), preserve the opening's ANCHOR:
//   • start   — keep the same distance from the START of the wall
//   • end     — keep the same distance from the END of the wall
//   • center  — always at (newLen / 2)
//
// Anchor is DETECTED from the old geometry. If the opening's reference
// point sits close to the wall's centre (within CENTER_TOL) → "center".
// Otherwise, whichever end it's closer to becomes the anchor.
//
// Reference point per kind:
//   Door with hinge_side="start" → position (near end of gap, at hinge)
//   Door with hinge_side="end"   → position (far end of gap = hinge)
//   Window                       → position (already the centre)
//   Open door                    → position + width/2 (middle of gap)
//
// Returns { doors, windows, open_doors } — merge into the draft with upd.
export function rescaleOpenings(drawing, changed, newVal) {
  if (!drawing) return {};
  const oldW = drawing.width || 0;
  const oldH = drawing.height || 0;
  if (oldW <= 0 || oldH <= 0) return {};

  // Which walls are affected by this change?
  const affectedWalls =
    changed === "width"
      ? new Set(["south", "north"])
      : new Set(["west", "east"]);

  const oldLen = changed === "width" ? oldW : oldH;
  const newLen = newVal;
  if (Math.abs(newLen - oldLen) < 1e-6) return {}; // no real change

  const CENTER_TOL = 0.3; // metres — how close to centre counts as "centre"

  // Given an opening's reference point ref on the old wall, its width w
  // (for openings that occupy a span), decide the anchor and return the
  // rescaled reference point on the new wall.
  const rescaleRef = (ref, w) => {
    const midOld = oldLen / 2;
    const distStart = ref;
    const distEnd = oldLen - ref;
    // Detect anchor
    if (Math.abs(ref - midOld) < CENTER_TOL) {
      // centred → snap to new centre
      return newLen / 2;
    }
    if (distStart <= distEnd) {
      // anchored to start — keep distance from start
      let r = distStart;
      // Clamp so the opening still fits on the new wall.
      if (r + w > newLen) r = Math.max(0, newLen - w);
      return r;
    }
    // anchored to end — keep distance from end
    let r = newLen - distEnd;
    if (r - w < 0) r = w;
    if (r > newLen) r = newLen;
    return r;
  };

  const rescaleDoor = (o) => {
    if (!affectedWalls.has(o.wall)) return o;
    // Reference point = the door's hinge position, which equals `position`
    // by the Python schema (regardless of hinge_side).
    const newRef = rescaleRef(o.position, o.width);
    return { ...o, position: newRef };
  };

  const rescaleWindow = (o) => {
    if (!affectedWalls.has(o.wall)) return o;
    // Window position is centre-based.
    // For center detection: check position (centre) against midOld directly.
    // For clamping: the full span is [pos - w/2, pos + w/2].
    const w = o.width || 1.4;
    const half = w / 2;
    // rescaleRef expects "position with width extending forward"; for a
    // window we want the CENTRE preserved as-is by anchor detection.
    const midOld = oldLen / 2;
    if (Math.abs(o.position - midOld) < CENTER_TOL) {
      return { ...o, position: newLen / 2 };
    }
    // else anchor to nearest end (dist from centre to that end)
    const distStart = o.position;
    const distEnd = oldLen - o.position;
    let newPos;
    if (distStart <= distEnd) {
      newPos = distStart;
    } else {
      newPos = newLen - distEnd;
    }
    // Clamp centre so full window fits
    if (newPos - half < 0) newPos = half;
    if (newPos + half > newLen) newPos = newLen - half;
    return { ...o, position: newPos };
  };

  const rescaleOpenDoor = (o) => {
    if (!affectedWalls.has(o.wall)) return o;
    // Reference point = middle of the gap (position + width/2).
    const w = o.width;
    const refOld = o.position + w / 2;
    const midOld = oldLen / 2;
    let newRefMid;
    if (Math.abs(refOld - midOld) < CENTER_TOL) {
      newRefMid = newLen / 2;
    } else {
      const distStart = refOld;
      const distEnd = oldLen - refOld;
      if (distStart <= distEnd) newRefMid = distStart;
      else newRefMid = newLen - distEnd;
    }
    let newPos = newRefMid - w / 2;
    if (newPos < 0) newPos = 0;
    if (newPos + w > newLen) newPos = Math.max(0, newLen - w);
    return { ...o, position: newPos };
  };

  return {
    doors: (drawing.doors || []).map(rescaleDoor),
    windows: (drawing.windows || []).map(rescaleWindow),
    open_doors: (drawing.open_doors || []).map(rescaleOpenDoor),
  };
}

// ── RESCALE OPENINGS for CUSTOM polygons — when a vertex drag or edit
// changes an edge's length, preserve each opening's anchor (start/end/
// center) exactly the same way rescaleOpenings does for rect rooms,
// generalized to "edgeN" tags and however many edges are actually
// affected by moving points (checks every edge, only touches ones
// whose length actually changed).
export function rescaleCustomOpenings(drawing, oldPoints, newPoints) {
  const closed = drawing.closed !== false;
  const nEdges = closed ? oldPoints.length : oldPoints.length - 1;
  const CENTER_TOL = 0.3;

  const edgeLen = (pts, i) => {
    const a = pts[i],
      b = pts[(i + 1) % pts.length];
    return Math.hypot(b[0] - a[0], b[1] - a[1]);
  };

  let doors = drawing.doors || [];
  let windows = drawing.windows || [];
  let open_doors = drawing.open_doors || [];

  for (let i = 0; i < nEdges; i++) {
    const oldLen = edgeLen(oldPoints, i);
    const newLen = edgeLen(newPoints, i);
    if (Math.abs(newLen - oldLen) < 1e-6) continue; // this edge unaffected
    const tag = `edge${i + 1}`;

    const rescaleRef = (ref, w) => {
      const midOld = oldLen / 2;
      const distStart = ref;
      const distEnd = oldLen - ref;
      if (Math.abs(ref - midOld) < CENTER_TOL) return newLen / 2;
      if (distStart <= distEnd) {
        let r = distStart;
        if (r + w > newLen) r = Math.max(0, newLen - w);
        return r;
      }
      let r = newLen - distEnd;
      if (r - w < 0) r = w;
      if (r > newLen) r = newLen;
      return r;
    };

    doors = doors.map((o) => {
      if (o.wall !== tag) return o;
      return { ...o, position: rescaleRef(o.position, o.width) };
    });

    windows = windows.map((o) => {
      if (o.wall !== tag) return o;
      const w = o.width || 1.4;
      const half = w / 2;
      const midOld = oldLen / 2;
      let newPos;
      if (Math.abs(o.position - midOld) < CENTER_TOL) {
        newPos = newLen / 2;
      } else {
        const distStart = o.position;
        const distEnd = oldLen - o.position;
        newPos = distStart <= distEnd ? distStart : newLen - distEnd;
      }
      if (newPos - half < 0) newPos = half;
      if (newPos + half > newLen) newPos = newLen - half;
      return { ...o, position: newPos };
    });

    open_doors = open_doors.map((o) => {
      if (o.wall !== tag) return o;
      const w = o.width;
      const refOld = o.position + w / 2;
      const midOld = oldLen / 2;
      let newRefMid;
      if (Math.abs(refOld - midOld) < CENTER_TOL) {
        newRefMid = newLen / 2;
      } else {
        const distStart = refOld;
        const distEnd = oldLen - refOld;
        newRefMid = distStart <= distEnd ? distStart : newLen - distEnd;
      }
      let newPos = newRefMid - w / 2;
      if (newPos < 0) newPos = 0;
      if (newPos + w > newLen) newPos = Math.max(0, newLen - w);
      return { ...o, position: newPos };
    });
  }

  return { doors, windows, open_doors };
}

// ═════════════════════════════════════════════════════════════════════════
//  CUSTOM POLYGON FORM  (Stage 3a)
// ═════════════════════════════════════════════════════════════════════════
//
// Two phases:
//   "points"    → user is placing the polygon corners on the canvas
//   "openings"  → polygon shape is done; ready for openings (Stage 3b)
//
// Openings sub-form is not yet built — will land in Stage 3b.

const SUBTYPES = [
  "BEDROOM",
  "BATHROOM",
  "KITCHEN",
  "LIVING",
  "DINING",
  "HALL",
  "STORAGE",
  "BALCONY",
];

function CustomForm({
  drawing,
  setDrawing,
  floor,
  editIndex,
  openingMode,
  setOpeningMode,
  openingDraft,
  setOpeningDraft,
  onCommitElement,
  onCancelEdit,
  inputStep = 0.1,
  setInputStep, // ← add this line
}) {
  const d = drawing;
  const upd = (patch) => setDrawing({ ...d, ...patch });

  const inPoints = d.polygonPhase === "points";
  const inOpenings = d.polygonPhase === "openings";
  const nPts = d.points?.length || 0;
  const canClose = nPts >= 3;
  const canOpen = nPts >= 2;

  // Delete a point by index.
  const deletePoint = (i) =>
    upd({ points: d.points.filter((_, j) => j !== i) });

  // Edit a point's x or y.
  // Edit a point's x or y.
  const editPoint = (i, xy, value) => {
    const v = parseFloat(value);
    const oldPoints = d.points;
    const next = [...oldPoints];
    const [px, py] = next[i];
    next[i] = xy === "x" ? [isNaN(v) ? px : v, py] : [px, isNaN(v) ? py : v];
    const rescaled = rescaleCustomOpenings(d, oldPoints, next);
    upd({ points: next, ...rescaled });
  };

  // ── Opening add helpers ─────────────────────────────────────────
  const startOpening = (kind) => {
    setOpeningMode(kind);
    setOpeningDraft({ kind });
  };
  const cancelOpening = () => {
    setOpeningMode(null);
    setOpeningDraft(null);
  };

  // Poly-cw follows the same rule as Python: for open polylines force
  // true (interior on the right of walking direction).
  const polyCw = !d.closed ? true : polygonIsClockwise(d.points || []);

  const commitCustomDoorOrWindow = () => {
    if (!openingDraft || !openingDraft.wall) return;
    if (openingDraft.kind === "door") {
      if (!openingDraft.width || !openingDraft.rotation) return;
      // Clamp position within the edge. The Python schema wants
      // position = hinge point along the edge.
      const L = openingDraft.edgeInfo?.edgeLen || 0;
      let pos = openingDraft.pos;
      const w = openingDraft.width;
      if (openingDraft.hinge_side === "end") {
        if (pos < w) pos = w;
        if (pos > L) pos = L;
      } else {
        if (pos < 0) pos = 0;
        if (pos + w > L) pos = Math.max(0, L - w);
      }
      const door = {
        wall: openingDraft.wall,
        position: pos,
        width: w,
        swing: openingDraft.swing || "right",
        hinge_side: openingDraft.hinge_side || "start",
        is_main: !!openingDraft.is_main,
        connects_to: "",
      };
      upd({ doors: [...(d.doors || []), door] });
    } else if (openingDraft.kind === "window") {
      const L = openingDraft.edgeInfo?.edgeLen || 0;
      let pos = openingDraft.pos;
      const w = openingDraft.width || 1.4;
      const half = w / 2;
      if (pos < half) pos = half;
      if (pos > L - half) pos = L - half;
      const win = {
        wall: openingDraft.wall,
        position: pos,
        width: w,
      };
      upd({ windows: [...(d.windows || []), win] });
    }
    cancelOpening();
  };

  const commitCustomOpenDoor = () => {
    if (
      !openingDraft ||
      !openingDraft.wall ||
      openingDraft.pos1 == null ||
      openingDraft.pos2 == null
    )
      return;
    const pos = Math.min(openingDraft.pos1, openingDraft.pos2);
    const wid = Math.abs(openingDraft.pos2 - openingDraft.pos1);
    if (wid < 0.05) {
      alert("Open door width is too small.");
      return;
    }
    upd({
      open_doors: [
        ...(d.open_doors || []),
        {
          wall: openingDraft.wall,
          position: pos,
          width: wid,
        },
      ],
    });
    cancelOpening();
  };

  // Human-readable edge label ("Edge 3") from stored wall="edgeN".
  const edgeLabel = (w) => {
    if (!w) return "?";
    const m = /^edge(\d+)$/.exec(w);
    return m ? `Edge ${m[1]}` : w;
  };

  return (
    <>
      <div
        style={{
          padding: 8,
          background: editIndex !== null ? "#fff8dc" : "#e0f2f1",
          border:
            editIndex !== null ? "1px solid #f0c040" : "1px solid #b2dfdb",
          borderRadius: 6,
          marginBottom: 12,
          fontSize: 13,
        }}
      >
        {editIndex !== null ? (
          <>
            Editing custom: <strong>{d.label || d.name}</strong>
          </>
        ) : (
          <>
            Drawing: <strong>CUSTOM polygon</strong> ({d.subtype})
          </>
        )}
      </div>

      <Row>
        <div style={{ flex: 1 }}>
          <Label>Name</Label>
          <Input
            value={d.name}
            onChange={(e) => upd({ name: e.target.value })}
            placeholder="e.g. main_hall"
          />
        </div>
        <div style={{ flex: 1 }}>
          <Label>Label (on plan)</Label>
          <Input
            value={d.label}
            onChange={(e) => upd({ label: e.target.value })}
            placeholder="e.g. MAIN HALL"
          />
        </div>
      </Row>

      <div style={{ marginBottom: 8 }}>
        <Label>Subtype (architectural)</Label>
        <select
          value={d.subtype || "HALL"}
          onChange={(e) => upd({ subtype: e.target.value })}
          style={{
            width: "100%",
            padding: 5,
            border: "1px solid #ccc",
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          {SUBTYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {/* ── POINTS PHASE ──────────────────────────────────────────────── */}
      {inPoints && (
        <Section>
          <H4>Polygon points ({nPts})</H4>

          <div
            style={{
              padding: 6,
              background: "#f8f4d5",
              border: "1px solid #ede4a3",
              borderRadius: 4,
              fontSize: 11,
              marginBottom: 8,
            }}
          >
            <strong>Click points on the canvas.</strong>
            <br />
            <span style={{ color: "#666" }}>
              <kbd>Enter</kbd> = close polygon, <kbd>O</kbd> = keep open,{" "}
              <kbd>Esc</kbd> = undo last point
            </span>
          </div>

          {nPts === 0 && (
            <div style={{ fontSize: 12, color: "#888" }}>
              (No points yet — click on the canvas to begin.)
            </div>
          )}

          {d.points.map((p, i) => (
            <Row key={i} style={{ marginBottom: 4 }}>
              <div style={{ flex: "0 0 22px", fontSize: 11, paddingTop: 6 }}>
                #{i + 1}
              </div>
              <Input
                type="number"
                value={p[0]}
                step={inputStep}
                onChange={(e) => editPoint(i, "x", e.target.value)}
                style={{ flex: 1 }}
              />
              <Input
                type="number"
                value={p[1]}
                step={inputStep}
                onChange={(e) => editPoint(i, "y", e.target.value)}
                style={{ flex: 1 }}
              />
              <DeleteBtn onClick={() => deletePoint(i)}>×</DeleteBtn>
            </Row>
          ))}

          <Row style={{ marginBottom: 12, alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, color: "#555" }}>Step:</span>
            <button
              type="button"
              onClick={() => setInputStep && setInputStep(0.1)}
              style={{
                padding: "3px 10px",
                fontSize: 12,
                background: inputStep === 0.1 ? "#2e8b57" : "#fff",
                color: inputStep === 0.1 ? "white" : "#333",
                border: "1px solid #bfdac9",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              0.1 m
            </button>
            <button
              type="button"
              onClick={() => setInputStep && setInputStep(0.01)}
              style={{
                padding: "3px 10px",
                fontSize: 12,
                background: inputStep === 0.01 ? "#2e8b57" : "#fff",
                color: inputStep === 0.01 ? "white" : "#333",
                border: "1px solid #bfdac9",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              0.01 m
            </button>
          </Row>

          <Row style={{ marginTop: 8 }}>
            <SmallBtn
              $primary
              disabled={!canClose}
              onClick={() => upd({ closed: true, polygonPhase: "openings" })}
            >
              Close polygon
            </SmallBtn>
            <SmallBtn
              disabled={!canOpen}
              onClick={() => upd({ closed: false, polygonPhase: "openings" })}
            >
              Keep open
            </SmallBtn>
          </Row>
        </Section>
      )}

      {/* ── OPENINGS PHASE (placeholder — Stage 3b) ──────────────────── */}
      {inOpenings && (
        <Section>
          <H4>
            Shape done ({nPts} points, {d.closed ? "closed" : "open"})
          </H4>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
            Polygon has {nPts} edges ({d.closed ? "closed" : "open"}). The
            polygon winding is{" "}
            <strong>{polyCw ? "clockwise" : "counterclockwise"}</strong>, which
            determines door swing direction (matches CLI convention).
          </div>

          {/* Existing openings — quick list with delete */}
          {(d.doors || []).map((o, i) => (
            <div key={`d${i}`} style={{ fontSize: 11, marginBottom: 3 }}>
              <Chip $type="door">DOOR</Chip>
              {edgeLabel(o.wall)} pos {o.position.toFixed(2)}m w {o.width}m
              <DeleteBtn
                onClick={() =>
                  upd({ doors: d.doors.filter((_, j) => j !== i) })
                }
              >
                ×
              </DeleteBtn>
            </div>
          ))}
          {(d.windows || []).map((o, i) => (
            <div key={`w${i}`} style={{ fontSize: 11, marginBottom: 3 }}>
              <Chip $type="window">WIN</Chip>
              {edgeLabel(o.wall)} centre {o.position.toFixed(2)}m w {o.width}m
              <DeleteBtn
                onClick={() =>
                  upd({ windows: d.windows.filter((_, j) => j !== i) })
                }
              >
                ×
              </DeleteBtn>
            </div>
          ))}
          {(d.open_doors || []).map((o, i) => (
            <div key={`o${i}`} style={{ fontSize: 11, marginBottom: 3 }}>
              <Chip $type="open">OPEN</Chip>
              {edgeLabel(o.wall)} pos {o.position.toFixed(2)}m w{" "}
              {o.width.toFixed(2)}m
              <DeleteBtn
                onClick={() =>
                  upd({ open_doors: d.open_doors.filter((_, j) => j !== i) })
                }
              >
                ×
              </DeleteBtn>
            </div>
          ))}

          {/* No opening being placed → show three "add" buttons */}
          {!openingMode && (
            <Row style={{ marginTop: 8, marginBottom: 0 }}>
              <SmallBtn $primary onClick={() => startOpening("door")}>
                + Door
              </SmallBtn>
              <SmallBtn $primary onClick={() => startOpening("window")}>
                + Window
              </SmallBtn>
              <SmallBtn $primary onClick={() => startOpening("open_door")}>
                + Open door
              </SmallBtn>
            </Row>
          )}

          {/* Placing a DOOR or WINDOW: waiting for an edge click */}
          {openingMode && !openingDraft?.wall && (
            <div
              style={{
                marginTop: 8,
                padding: 8,
                background: "#fff3cd",
                border: "1px solid #ffeeba",
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              Click on an <strong>edge</strong> of the polygon to place the{" "}
              <strong>{openingMode.replace("_", " ")}</strong>.
              <SmallBtn onClick={cancelOpening} style={{ float: "right" }}>
                Cancel
              </SmallBtn>
            </div>
          )}

          {/* DOOR form after the hinge point is clicked */}
          {openingMode === "door" && openingDraft?.wall && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, marginBottom: 6 }}>
                {edgeLabel(openingDraft.wall)}, hinge at{" "}
                {openingDraft.pos.toFixed(2)}m along edge
              </div>
              <Label>Width (m) — range 0.7 to 2.0</Label>
              <Input
                type="number"
                min="0.7"
                max="2.0"
                step={inputStep}
                value={openingDraft.width || 1.1}
                onChange={(e) => {
                  let w = parseFloat(e.target.value) || 0;
                  if (w > 0 && w < 0.7) w = 0.7;
                  if (w > 2.0) w = 2.0;
                  setOpeningDraft({ ...openingDraft, width: w });
                }}
              />
              <Label style={{ marginTop: 6 }}>Rotation (open → close)</Label>
              <Row style={{ marginBottom: 6 }}>
                <label style={{ flex: 1, fontSize: 12 }}>
                  <input
                    type="radio"
                    checked={openingDraft.rotation === "clockwise"}
                    onChange={() => {
                      const { swing, hingeSide } = edgeSwing(
                        polyCw,
                        "clockwise"
                      );
                      setOpeningDraft({
                        ...openingDraft,
                        rotation: "clockwise",
                        swing,
                        hinge_side: hingeSide,
                      });
                    }}
                  />{" "}
                  CW
                </label>
                <label style={{ flex: 1, fontSize: 12 }}>
                  <input
                    type="radio"
                    checked={openingDraft.rotation === "counterclockwise"}
                    onChange={() => {
                      const { swing, hingeSide } = edgeSwing(
                        polyCw,
                        "counterclockwise"
                      );
                      setOpeningDraft({
                        ...openingDraft,
                        rotation: "counterclockwise",
                        swing,
                        hinge_side: hingeSide,
                      });
                    }}
                  />{" "}
                  CCW
                </label>
              </Row>
              <Row style={{ marginTop: 8 }}>
                <SmallBtn
                  $primary
                  onClick={commitCustomDoorOrWindow}
                  disabled={!openingDraft.rotation}
                >
                  Add door
                </SmallBtn>
                <SmallBtn onClick={cancelOpening}>Cancel</SmallBtn>
              </Row>
            </div>
          )}

          {/* WINDOW form after centre click */}
          {openingMode === "window" && openingDraft?.wall && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, marginBottom: 6 }}>
                {edgeLabel(openingDraft.wall)}, centre at{" "}
                {openingDraft.pos.toFixed(2)}m along edge
              </div>
              <Label>Width (m) — range 0.3 to 1.6</Label>
              <Input
                type="number"
                min="0.3"
                max="1.6"
                step={inputStep}
                value={openingDraft.width || 1.4}
                onChange={(e) => {
                  let w = parseFloat(e.target.value) || 0;
                  if (w > 0 && w < 0.3) w = 0.3;
                  if (w > 1.6) w = 1.6;
                  setOpeningDraft({ ...openingDraft, width: w });
                }}
              />
              <Row style={{ marginTop: 8 }}>
                <SmallBtn $primary onClick={commitCustomDoorOrWindow}>
                  Add window
                </SmallBtn>
                <SmallBtn onClick={cancelOpening}>Cancel</SmallBtn>
              </Row>
            </div>
          )}

          {/* OPEN DOOR: needs two clicks on the SAME edge */}
          {openingMode === "open_door" && openingDraft && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              {openingDraft.pos1 == null && (
                <>
                  Click <strong>begin point</strong> on any edge.
                </>
              )}
              {openingDraft.pos1 != null && openingDraft.pos2 == null && (
                <>
                  Begin: {edgeLabel(openingDraft.wall)} @{" "}
                  {openingDraft.pos1.toFixed(2)}m. Now click{" "}
                  <strong>end point</strong> on the same edge.
                </>
              )}
              {openingDraft.pos2 != null && (
                <div>
                  {edgeLabel(openingDraft.wall)}: {openingDraft.pos1.toFixed(2)}
                  m → {openingDraft.pos2.toFixed(2)}m (width{" "}
                  {Math.abs(openingDraft.pos2 - openingDraft.pos1).toFixed(2)}m)
                  <Row style={{ marginTop: 6 }}>
                    <SmallBtn $primary onClick={commitCustomOpenDoor}>
                      Add open door
                    </SmallBtn>
                    <SmallBtn onClick={cancelOpening}>Cancel</SmallBtn>
                  </Row>
                </div>
              )}
            </div>
          )}

          <SmallBtn
            onClick={() => upd({ polygonPhase: "points" })}
            style={{ marginTop: 8 }}
          >
            ← Back to editing points
          </SmallBtn>
        </Section>
      )}

      {/* Commit / cancel */}
      {editIndex !== null ? (
        <>
          <AddBtn
            disabled={!(d.name && inOpenings)}
            onClick={onCommitElement}
            style={{ background: d.name && inOpenings ? "#2e8b57" : "#ccc" }}
          >
            {d.name && inOpenings
              ? "Save changes"
              : "Finish points (Enter) + fill name"}
          </AddBtn>
          <button
            onClick={onCancelEdit}
            style={{
              width: "100%",
              marginTop: 6,
              padding: "8px 10px",
              background: "#f4f4f4",
              color: "#333",
              border: "1px solid #ccc",
              borderRadius: 6,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Cancel edit
          </button>
        </>
      ) : (
        <AddBtn disabled={!(d.name && inOpenings)} onClick={onCommitElement}>
          {d.name && inOpenings
            ? "Add polygon to plan"
            : "Finish points (Enter) + fill name"}
        </AddBtn>
      )}

      {/* The "Elements in plan" list has been moved to a stand-alone
          component (ElementsList) rendered by the page below this form. */}
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  STAIR SHAFT FORM  (Stage 3c-1 — elevators come in 3c-2)
// ═════════════════════════════════════════════════════════════════════════
//
// The shaft rectangle size is COMPUTED from the shape parameters, NOT
// entered directly. So the user picks position + shape + rotation, and
// the block dimensions appear read-only below.
//
// Shaft doors are MAIN doors (is_main=true, swing OUTSIDE the shaft).
// The existing swingFromRotation / hingeFromRotation helpers already
// have the isMain=true branch that flips hinge_side per Python's
// _hinge_side_main table.

function ShaftForm({
  drawing,
  setDrawing,
  floor,
  editIndex,
  openingMode,
  setOpeningMode,
  openingDraft,
  setOpeningDraft,
  onCommitElement,
  onCancelEdit,
  inputStep = 0.1,
}) {
  const d = drawing;
  const upd = (patch) => setDrawing({ ...d, ...patch });

  // Derived rectangle & direction — same math as Python StairShaft props
  // and the CLI's up_dir computation (main.py lines 489-492).
  const der = shaftDerived(d);

  // ── Opening add helpers ────────────────────────────────────────────
  const startOpening = (kind) => {
    setOpeningMode(kind);
    setOpeningDraft({ kind });
  };
  const cancelOpening = () => {
    setOpeningMode(null);
    setOpeningDraft(null);
  };

  // For clamping openings on shaft walls: the "wall length" depends on
  // which wall — same as rectangles.
  const wallLen = (wall) =>
    ["south", "north"].includes(wall) ? der.blockW : der.blockH;

  const commitDoorOrWindow = () => {
    if (!openingDraft || !openingDraft.wall || !openingDraft.width) return;
    if (openingDraft.kind === "door") {
      const L = wallLen(openingDraft.wall);
      let pos = openingDraft.pos;
      const w = openingDraft.width;
      // Clamp — for hinge_side="end" gap goes backward, otherwise forward
      if (openingDraft.hinge_side === "end") {
        if (pos < w) pos = w;
        if (pos > L) pos = L;
      } else {
        if (pos < 0) pos = 0;
        if (pos + w > L) pos = Math.max(0, L - w);
      }
      const door = {
        wall: openingDraft.wall,
        position: pos,
        width: w,
        swing: openingDraft.swing || "right",
        hinge_side: openingDraft.hinge_side || "start",
        is_main: !!openingDraft.is_main, // user's choice — default true for shafts
        connects_to: "",
      };
      upd({ doors: [...(d.doors || []), door] });
    } else if (openingDraft.kind === "window") {
      const L = wallLen(openingDraft.wall);
      let pos = openingDraft.pos;
      const w = openingDraft.width || 1.4;
      const half = w / 2;
      if (pos < half) pos = half;
      if (pos > L - half) pos = L - half;
      upd({
        windows: [
          ...(d.windows || []),
          { wall: openingDraft.wall, position: pos, width: w },
        ],
      });
    }
    cancelOpening();
  };

  const commitOpenDoor = () => {
    if (
      !openingDraft ||
      !openingDraft.wall ||
      openingDraft.pos1 == null ||
      openingDraft.pos2 == null
    )
      return;
    const pos = Math.min(openingDraft.pos1, openingDraft.pos2);
    const wid = Math.abs(openingDraft.pos2 - openingDraft.pos1);
    if (wid < 0.05) {
      alert("Open door width is too small.");
      return;
    }
    upd({
      open_doors: [
        ...(d.open_doors || []),
        { wall: openingDraft.wall, position: pos, width: wid },
      ],
    });
    cancelOpening();
  };

  const isEditing = editIndex !== null;
  const canAdd = d.name && der.blockW >= 2 && der.blockH >= 2;

  return (
    <>
      <div
        style={{
          padding: 8,
          background: isEditing ? "#fff8dc" : "#e0f2f1",
          border: isEditing ? "1px solid #f0c040" : "1px solid #b2dfdb",
          borderRadius: 6,
          marginBottom: 12,
          fontSize: 13,
        }}
      >
        {isEditing ? (
          <>
            Editing shaft: <strong>{d.label || d.name}</strong>
          </>
        ) : (
          <>
            Drawing: <strong>STAIR SHAFT</strong> (بيت الدرج)
          </>
        )}
      </div>

      <Row>
        <div style={{ flex: 1 }}>
          <Label>Name</Label>
          <Input
            value={d.name}
            onChange={(e) => upd({ name: e.target.value })}
            placeholder="e.g. shaft1"
          />
        </div>
        <div style={{ flex: 1 }}>
          <Label>Label (on plan)</Label>
          <Input
            value={d.label}
            onChange={(e) => upd({ label: e.target.value })}
            placeholder="e.g. STAIR SHAFT"
          />
        </div>
      </Row>

      <Section>
        <H4>Position — bottom-left corner</H4>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>
          Click on the canvas to place the SW corner, or type below.
        </div>
        <Row>
          <div style={{ flex: 1 }}>
            <Label>x (m)</Label>
            <Input
              type="number"
              step={inputStep}
              value={d.x}
              onChange={(e) => upd({ x: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label>y (m)</Label>
            <Input
              type="number"
              step={inputStep}
              value={d.y}
              onChange={(e) => upd({ y: parseFloat(e.target.value) || 0 })}
            />
          </div>
        </Row>
      </Section>

      <Section>
        <H4>Shape — the rectangle size is computed</H4>

        <Row>
          <div style={{ flex: 1 }}>
            <Label>Total steps (split into 2 flights)</Label>
            <Input
              type="number"
              min="2"
              step="2"
              value={d.steps}
              onChange={(e) => upd({ steps: parseInt(e.target.value) || 2 })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label>Step depth (m)</Label>
            <Input
              type="number"
              min="0.2"
              step="0.05"
              value={d.depth}
              onChange={(e) =>
                upd({ depth: parseFloat(e.target.value) || 0.3 })
              }
            />
          </div>
        </Row>

        <Row>
          <div style={{ flex: 1 }}>
            <Label>Step width — one flight (m)</Label>
            <Input
              type="number"
              min="0.6"
              step={inputStep}
              value={d.step_width}
              onChange={(e) =>
                upd({ step_width: parseFloat(e.target.value) || 1.5 })
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label>Separation between flights (m)</Label>
            <Input
              type="number"
              min="0"
              step={inputStep}
              value={d.separation}
              onChange={(e) =>
                upd({ separation: parseFloat(e.target.value) || 0.3 })
              }
            />
          </div>
        </Row>

        <Row>
          <div style={{ flex: 1 }}>
            <Label>Flat landing depth — both ends (m)</Label>
            <Input
              type="number"
              min="0"
              step={inputStep}
              value={d.flat}
              onChange={(e) => upd({ flat: parseFloat(e.target.value) || 1.5 })}
            />
          </div>
        </Row>

        <Label style={{ marginTop: 4 }}>
          Width direction (step width axis)
        </Label>
        <Row style={{ marginBottom: 6 }}>
          <label style={{ flex: 1, fontSize: 12 }}>
            <input
              type="radio"
              checked={d.width_dir === "x"}
              onChange={() => upd({ width_dir: "x" })}
            />{" "}
            along X (horizontal)
          </label>
          <label style={{ flex: 1, fontSize: 12 }}>
            <input
              type="radio"
              checked={d.width_dir === "y"}
              onChange={() => upd({ width_dir: "y" })}
            />{" "}
            along Y (vertical)
          </label>
        </Row>

        <Label>Rotation of the climb (going UP)</Label>
        <Row style={{ marginBottom: 6 }}>
          <label style={{ flex: 1, fontSize: 12 }}>
            <input
              type="radio"
              checked={d.rotation === "clockwise"}
              onChange={() => upd({ rotation: "clockwise" })}
            />{" "}
            CW
          </label>
          <label style={{ flex: 1, fontSize: 12 }}>
            <input
              type="radio"
              checked={d.rotation === "counterclockwise"}
              onChange={() => upd({ rotation: "counterclockwise" })}
            />{" "}
            CCW
          </label>
        </Row>

        {/* Computed read-out — matches Python's block_w / block_h props */}
        <div
          style={{
            padding: 6,
            background: "#eef7f0",
            border: "1px solid #bfdac9",
            borderRadius: 4,
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          <div>
            Rectangle:{" "}
            <strong>
              {der.blockW.toFixed(2)} × {der.blockH.toFixed(2)} m
            </strong>
          </div>
          <div>
            Run length: {der.runLen.toFixed(2)}m ({Math.floor(der.steps / 2)}{" "}
            steps × {der.depth} + 2 × {der.flat})
          </div>
          <div>
            Total width: {der.widthTotal.toFixed(2)}m (2 × {der.stepWidth} +{" "}
            {der.separation})
          </div>
          <div>
            First flight climbs: <strong>{der.upDir.toUpperCase()}</strong>
          </div>
        </div>
      </Section>

      {/* ── Openings section ────────────────────────────────────────── */}
      <Section>
        <H4>Main doors, windows, open doors</H4>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>
          Shaft doors are <strong>MAIN doors by default</strong> — leaf swings
          OUTSIDE the shaft. You can uncheck "Main door" for a regular door that
          opens INTO the shaft.
        </div>

        {(d.doors || []).map((o, i) => (
          <div key={`d${i}`} style={{ fontSize: 11, marginBottom: 3 }}>
            <Chip $type="door">{o.is_main ? "MAIN" : "DOOR"}</Chip>
            {wallLabel(o.wall)} pos {o.position.toFixed(2)}m w {o.width}m
            <DeleteBtn
              onClick={() => upd({ doors: d.doors.filter((_, j) => j !== i) })}
            >
              ×
            </DeleteBtn>
          </div>
        ))}
        {(d.windows || []).map((o, i) => (
          <div key={`w${i}`} style={{ fontSize: 11, marginBottom: 3 }}>
            <Chip $type="window">WIN</Chip>
            {wallLabel(o.wall)} centre {o.position.toFixed(2)}m w {o.width}m
            <DeleteBtn
              onClick={() =>
                upd({ windows: d.windows.filter((_, j) => j !== i) })
              }
            >
              ×
            </DeleteBtn>
          </div>
        ))}
        {(d.open_doors || []).map((o, i) => (
          <div key={`o${i}`} style={{ fontSize: 11, marginBottom: 3 }}>
            <Chip $type="open">OPEN</Chip>
            {wallLabel(o.wall)} pos {o.position.toFixed(2)}m w{" "}
            {o.width.toFixed(2)}m
            <DeleteBtn
              onClick={() =>
                upd({ open_doors: d.open_doors.filter((_, j) => j !== i) })
              }
            >
              ×
            </DeleteBtn>
          </div>
        ))}

        {!openingMode && (
          <Row style={{ marginTop: 8, marginBottom: 0 }}>
            <SmallBtn $primary onClick={() => startOpening("door")}>
              + Door
            </SmallBtn>
            <SmallBtn $primary onClick={() => startOpening("window")}>
              + Window
            </SmallBtn>
            <SmallBtn $primary onClick={() => startOpening("open_door")}>
              + Open door
            </SmallBtn>
          </Row>
        )}

        {openingMode && !openingDraft?.wall && (
          <div
            style={{
              marginTop: 8,
              padding: 8,
              background: "#fff3cd",
              border: "1px solid #ffeeba",
              borderRadius: 4,
              fontSize: 12,
            }}
          >
            Click on a wall of the shaft to place the{" "}
            <strong>{openingMode.replace("_", " ")}</strong>.
            <SmallBtn onClick={cancelOpening} style={{ float: "right" }}>
              Cancel
            </SmallBtn>
          </div>
        )}

        {/* DOOR form after hinge click */}
        {openingMode === "door" && openingDraft?.wall && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, marginBottom: 6 }}>
              Wall: <strong>{wallLabel(openingDraft.wall)}</strong>, hinge at{" "}
              {openingDraft.pos.toFixed(2)}m
            </div>
            <Label>Width (m) — range 0.7 to 2.0</Label>
            <Input
              type="number"
              min="0.7"
              max="2.0"
              step={inputStep}
              value={openingDraft.width || 1.1}
              onChange={(e) => {
                let w = parseFloat(e.target.value) || 0;
                if (w > 0 && w < 0.7) w = 0.7;
                if (w > 2.0) w = 2.0;
                setOpeningDraft({ ...openingDraft, width: w });
              }}
            />

            {/* Main-door toggle — default true for shafts but user can
                make it a regular door that opens INTO the shaft. */}
            <label
              style={{
                display: "block",
                marginTop: 8,
                fontSize: 12,
                padding: 6,
                background: "#eef7f0",
                border: "1px solid #bfdac9",
                borderRadius: 4,
              }}
            >
              <input
                type="checkbox"
                checked={openingDraft.is_main !== false}
                onChange={(e) => {
                  const isMain = e.target.checked;
                  // Recompute swing/hinge if rotation is already picked
                  let patch = { ...openingDraft, is_main: isMain };
                  if (openingDraft.rotation) {
                    patch.swing = swingFromRotation(
                      openingDraft.wall,
                      openingDraft.rotation,
                      isMain
                    );
                    patch.hinge_side = hingeFromRotation(
                      openingDraft.wall,
                      openingDraft.rotation,
                      isMain
                    );
                  }
                  setOpeningDraft(patch);
                }}
              />{" "}
              <strong>Main door</strong> — leaf swings OUTSIDE the shaft
              <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                Uncheck for a regular door (opens INTO the shaft — e.g. for a
                utility closet with its opening on the outside)
              </div>
            </label>

            <Label style={{ marginTop: 8 }}>Rotation (open → close)</Label>
            <Row style={{ marginBottom: 6 }}>
              <label style={{ flex: 1, fontSize: 12 }}>
                <input
                  type="radio"
                  checked={openingDraft.rotation === "clockwise"}
                  onChange={() => {
                    const isMain = openingDraft.is_main !== false;
                    const swing = swingFromRotation(
                      openingDraft.wall,
                      "clockwise",
                      isMain
                    );
                    const hinge = hingeFromRotation(
                      openingDraft.wall,
                      "clockwise",
                      isMain
                    );
                    setOpeningDraft({
                      ...openingDraft,
                      rotation: "clockwise",
                      swing,
                      hinge_side: hinge,
                    });
                  }}
                />{" "}
                CW
              </label>
              <label style={{ flex: 1, fontSize: 12 }}>
                <input
                  type="radio"
                  checked={openingDraft.rotation === "counterclockwise"}
                  onChange={() => {
                    const isMain = openingDraft.is_main !== false;
                    const swing = swingFromRotation(
                      openingDraft.wall,
                      "counterclockwise",
                      isMain
                    );
                    const hinge = hingeFromRotation(
                      openingDraft.wall,
                      "counterclockwise",
                      isMain
                    );
                    setOpeningDraft({
                      ...openingDraft,
                      rotation: "counterclockwise",
                      swing,
                      hinge_side: hinge,
                    });
                  }}
                />{" "}
                CCW
              </label>
            </Row>
            <Row style={{ marginTop: 8 }}>
              <SmallBtn
                $primary
                onClick={commitDoorOrWindow}
                disabled={!openingDraft.rotation}
              >
                {openingDraft.is_main !== false ? "Add main door" : "Add door"}
              </SmallBtn>
              <SmallBtn onClick={cancelOpening}>Cancel</SmallBtn>
            </Row>
          </div>
        )}

        {/* WINDOW form */}
        {openingMode === "window" && openingDraft?.wall && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, marginBottom: 6 }}>
              Wall: <strong>{wallLabel(openingDraft.wall)}</strong>, centre at{" "}
              {openingDraft.pos.toFixed(2)}m
            </div>
            <Label>Width (m) — range 0.3 to 1.6</Label>
            <Input
              type="number"
              min="0.3"
              max="1.6"
              step={inputStep}
              value={openingDraft.width || 1.4}
              onChange={(e) => {
                let w = parseFloat(e.target.value) || 0;
                if (w > 0 && w < 0.3) w = 0.3;
                if (w > 1.6) w = 1.6;
                setOpeningDraft({ ...openingDraft, width: w });
              }}
            />
            <Row style={{ marginTop: 8 }}>
              <SmallBtn $primary onClick={commitDoorOrWindow}>
                Add window
              </SmallBtn>
              <SmallBtn onClick={cancelOpening}>Cancel</SmallBtn>
            </Row>
          </div>
        )}

        {/* OPEN DOOR — two clicks */}
        {openingMode === "open_door" && openingDraft && (
          <div style={{ marginTop: 8, fontSize: 12 }}>
            {openingDraft.pos1 == null && (
              <>
                Click <strong>begin point</strong> on any wall.
              </>
            )}
            {openingDraft.pos1 != null && openingDraft.pos2 == null && (
              <>
                Begin: {wallLabel(openingDraft.wall)} @{" "}
                {openingDraft.pos1.toFixed(2)}m. Now click{" "}
                <strong>end point</strong> on the same wall.
              </>
            )}
            {openingDraft.pos2 != null && (
              <div>
                {wallLabel(openingDraft.wall)}: {openingDraft.pos1.toFixed(2)}m
                → {openingDraft.pos2.toFixed(2)}m (width{" "}
                {Math.abs(openingDraft.pos2 - openingDraft.pos1).toFixed(2)}m)
                <Row style={{ marginTop: 6 }}>
                  <SmallBtn $primary onClick={commitOpenDoor}>
                    Add open door
                  </SmallBtn>
                  <SmallBtn onClick={cancelOpening}>Cancel</SmallBtn>
                </Row>
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ── Elevators section ────────────────────────────────────── */}
      <Section>
        <H4>Elevators inside the shaft</H4>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>
          Each elevator is a small rectangle with one open side. Defaults face
          the shaft's SOUTH.
        </div>

        {(d.elevators || []).map((e, i) => (
          <div
            key={`e${i}`}
            style={{
              padding: 6,
              marginBottom: 6,
              border: "1px solid #d0d7de",
              borderRadius: 4,
              background: "#f6f8fa",
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>
              Elevator #{i + 1}
              <DeleteBtn
                style={{ float: "right" }}
                onClick={() =>
                  upd({
                    elevators: d.elevators.filter((_, j) => j !== i),
                  })
                }
              >
                ×
              </DeleteBtn>
            </div>
            <Row>
              <div style={{ flex: 1 }}>
                <Label>x (m)</Label>
                <Input
                  type="number"
                  step={inputStep}
                  value={e.x}
                  onChange={(ev) => {
                    const next = [...d.elevators];
                    next[i] = { ...e, x: parseFloat(ev.target.value) || 0 };
                    upd({ elevators: next });
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Label>y (m)</Label>
                <Input
                  type="number"
                  step={inputStep}
                  value={e.y}
                  onChange={(ev) => {
                    const next = [...d.elevators];
                    next[i] = { ...e, y: parseFloat(ev.target.value) || 0 };
                    upd({ elevators: next });
                  }}
                />
              </div>
            </Row>
            <Row>
              <div style={{ flex: 1 }}>
                <Label>Width → X (m)</Label>
                <Input
                  type="number"
                  min="0.5"
                  step={inputStep}
                  value={e.width}
                  onChange={(ev) => {
                    const next = [...d.elevators];
                    next[i] = {
                      ...e,
                      width: parseFloat(ev.target.value) || 1.6,
                    };
                    upd({ elevators: next });
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Label>Height ↑ Y (m)</Label>
                <Input
                  type="number"
                  min="0.5"
                  step={inputStep}
                  value={e.height}
                  onChange={(ev) => {
                    const next = [...d.elevators];
                    next[i] = {
                      ...e,
                      height: parseFloat(ev.target.value) || 2.0,
                    };
                    upd({ elevators: next });
                  }}
                />
              </div>
            </Row>
            <Row>
              <div style={{ flex: 1 }}>
                <Label>Opening side</Label>
                <select
                  value={e.opening_side || "south"}
                  onChange={(ev) => {
                    const next = [...d.elevators];
                    next[i] = { ...e, opening_side: ev.target.value };
                    upd({ elevators: next });
                  }}
                  style={{
                    width: "100%",
                    padding: 5,
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                >
                  <option value="south">south (facing floor)</option>
                  <option value="north">north</option>
                  <option value="east">east</option>
                  <option value="west">west</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <Label>Opening width (m)</Label>
                <Input
                  type="number"
                  min="0.3"
                  max="1.6"
                  step={inputStep}
                  value={e.opening_width ?? 0.9}
                  onChange={(ev) => {
                    let w = parseFloat(ev.target.value) || 0.9;
                    if (w < 0.3) w = 0.3;
                    if (w > 1.6) w = 1.6;
                    const next = [...d.elevators];
                    next[i] = { ...e, opening_width: w };
                    upd({ elevators: next });
                  }}
                />
              </div>
            </Row>
          </div>
        ))}

        <SmallBtn
          $primary
          onClick={() => {
            // Default new elevator: at the top-left corner of the shaft
            // (matches CLI defaults at main.py lines 521-523 which use
            // shaft.x and shaft.y1). shaft.y1 = shaft.y + block_h.
            const newElev = {
              x: d.x,
              y: d.y + der.blockH,
              width: 1.6,
              height: 2.0,
              opening_side: "south",
              opening_width: 0.9,
            };
            upd({ elevators: [...(d.elevators || []), newElev] });
          }}
        >
          + Add elevator
        </SmallBtn>
      </Section>

      {/* Commit / cancel */}
      {isEditing ? (
        <>
          <AddBtn
            disabled={!canAdd}
            onClick={onCommitElement}
            style={{ background: canAdd ? "#2e8b57" : "#ccc" }}
          >
            {canAdd
              ? "Save changes"
              : !d.name
                ? "Fill name to save"
                : "Shape must be ≥ 2 × 2 m — increase steps or dims"}
          </AddBtn>
          <button
            onClick={onCancelEdit}
            style={{
              width: "100%",
              marginTop: 6,
              padding: "8px 10px",
              background: "#f4f4f4",
              color: "#333",
              border: "1px solid #ccc",
              borderRadius: 6,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Cancel edit
          </button>
        </>
      ) : (
        <AddBtn disabled={!canAdd} onClick={onCommitElement}>
          {canAdd
            ? "Add shaft to plan"
            : !d.name
              ? "Fill name to add"
              : "Shape must be ≥ 2 × 2 m — increase steps or dims"}
        </AddBtn>
      )}
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  SIMPLE STAIRS FORM  (Stage 3h — outdoor single-flight stairs)
// ═════════════════════════════════════════════════════════════════════════
//
// Simpler than SHAFT: one straight flight instead of a U-shape.
// - No separation, no flat landings, no elevators
// - Openings: main doors + open doors ONLY (no windows in Python schema)
// - Size rules (per user): width ≥ 2 m, steps ≥ 2, depth ≥ 0.3 m
// - Rescaling openings on width/step change: OFF (per user)

function StairsForm({
  drawing,
  setDrawing,
  floor,
  editIndex,
  openingMode,
  setOpeningMode,
  openingDraft,
  setOpeningDraft,
  onCommitElement,
  onCancelEdit,
  inputStep = 0.1,
}) {
  const d = drawing;
  const upd = (patch) => setDrawing({ ...d, ...patch });

  const der = stairsDerived(d);

  const startOpening = (kind) => {
    setOpeningMode(kind);
    setOpeningDraft({ kind });
  };
  const cancelOpening = () => {
    setOpeningMode(null);
    setOpeningDraft(null);
  };

  const wallLen = (wall) =>
    ["south", "north"].includes(wall) ? der.blockW : der.blockH;

  const commitDoor = () => {
    if (!openingDraft || !openingDraft.wall || !openingDraft.width) return;
    const L = wallLen(openingDraft.wall);
    let pos = openingDraft.pos;
    const w = openingDraft.width;
    if (openingDraft.hinge_side === "end") {
      if (pos < w) pos = w;
      if (pos > L) pos = L;
    } else {
      if (pos < 0) pos = 0;
      if (pos + w > L) pos = Math.max(0, L - w);
    }
    const door = {
      wall: openingDraft.wall,
      position: pos,
      width: w,
      swing: openingDraft.swing || "right",
      hinge_side: openingDraft.hinge_side || "start",
      is_main: !!openingDraft.is_main,
      connects_to: "",
    };
    upd({ doors: [...(d.doors || []), door] });
    cancelOpening();
  };

  const commitOpenDoor = () => {
    if (
      !openingDraft ||
      !openingDraft.wall ||
      openingDraft.pos1 == null ||
      openingDraft.pos2 == null
    )
      return;
    const pos = Math.min(openingDraft.pos1, openingDraft.pos2);
    const wid = Math.abs(openingDraft.pos2 - openingDraft.pos1);
    if (wid < 0.05) {
      alert("Open door width is too small.");
      return;
    }
    upd({
      open_doors: [
        ...(d.open_doors || []),
        { wall: openingDraft.wall, position: pos, width: wid },
      ],
    });
    cancelOpening();
  };

  const isEditing = editIndex !== null;
  // Q2 = literal size rule: width ≥ 2, steps ≥ 2, depth ≥ 0.3.
  // No additional 2×2 block check (per user — some humans can't climb steps).
  const canAdd = d.name && d.width >= 2 && d.steps >= 2 && d.depth >= 0.3;

  return (
    <>
      <div
        style={{
          padding: 8,
          background: isEditing ? "#fff8dc" : "#e0f2f1",
          border: isEditing ? "1px solid #f0c040" : "1px solid #b2dfdb",
          borderRadius: 6,
          marginBottom: 12,
          fontSize: 13,
        }}
      >
        {isEditing ? (
          <>
            Editing stairs: <strong>{d.label || d.name}</strong>
          </>
        ) : (
          <>
            Drawing: <strong>STAIRS</strong> (outdoor / simple flight)
          </>
        )}
      </div>

      <Row>
        <div style={{ flex: 1 }}>
          <Label>Name</Label>
          <Input
            value={d.name}
            onChange={(e) => upd({ name: e.target.value })}
            placeholder="e.g. stairs1"
          />
        </div>
        <div style={{ flex: 1 }}>
          <Label>Label (on plan)</Label>
          <Input
            value={d.label}
            onChange={(e) => upd({ label: e.target.value })}
            placeholder="e.g. STAIRS"
          />
        </div>
      </Row>

      <Section>
        <H4>Position — bottom-left corner</H4>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>
          Click on the canvas to place the SW corner, or type below.
        </div>
        <Row>
          <div style={{ flex: 1 }}>
            <Label>x (m)</Label>
            <Input
              type="number"
              step={inputStep}
              value={d.x}
              onChange={(e) => upd({ x: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label>y (m)</Label>
            <Input
              type="number"
              step={inputStep}
              value={d.y}
              onChange={(e) => upd({ y: parseFloat(e.target.value) || 0 })}
            />
          </div>
        </Row>
      </Section>

      <Section>
        <H4>Shape — one straight flight</H4>

        <Row>
          <div style={{ flex: 1 }}>
            <Label>Steps — min 2</Label>
            <Input
              type="number"
              min="2"
              step="1"
              value={d.steps}
              onChange={(e) => {
                let n = parseInt(e.target.value) || 2;
                if (n < 2) n = 2;
                upd({ steps: n });
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label>Step depth (m) — min 0.3</Label>
            <Input
              type="number"
              min="0.3"
              step="0.05"
              value={d.depth}
              onChange={(e) => {
                let dp = parseFloat(e.target.value) || 0.3;
                if (dp < 0.3) dp = 0.3;
                upd({ depth: dp });
              }}
            />
          </div>
        </Row>

        <Row>
          <div style={{ flex: 1 }}>
            <Label>Width (m) — min 2.0</Label>
            <Input
              type="number"
              min="2"
              step={inputStep}
              value={d.width}
              onChange={(e) => {
                let w = parseFloat(e.target.value) || 0;
                if (w > 0 && w < 2) w = 2;
                upd({ width: w });
              }}
            />
          </div>
        </Row>

        <Label style={{ marginTop: 4 }}>
          Width direction (step width axis)
        </Label>
        <Row style={{ marginBottom: 6 }}>
          <label style={{ flex: 1, fontSize: 12 }}>
            <input
              type="radio"
              checked={d.width_dir === "x"}
              onChange={() => upd({ width_dir: "x" })}
            />{" "}
            along X (horizontal)
          </label>
          <label style={{ flex: 1, fontSize: 12 }}>
            <input
              type="radio"
              checked={d.width_dir === "y"}
              onChange={() => upd({ width_dir: "y" })}
            />{" "}
            along Y (vertical)
          </label>
        </Row>

        <Label>Rotation of the climb (going UP)</Label>
        <Row style={{ marginBottom: 6 }}>
          <label style={{ flex: 1, fontSize: 12 }}>
            <input
              type="radio"
              checked={d.rotation === "clockwise"}
              onChange={() => upd({ rotation: "clockwise" })}
            />{" "}
            CW
          </label>
          <label style={{ flex: 1, fontSize: 12 }}>
            <input
              type="radio"
              checked={d.rotation === "counterclockwise"}
              onChange={() => upd({ rotation: "counterclockwise" })}
            />{" "}
            CCW
          </label>
        </Row>

        <div
          style={{
            padding: 6,
            background: "#eef7f0",
            border: "1px solid #bfdac9",
            borderRadius: 4,
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          <div>
            Rectangle:{" "}
            <strong>
              {der.blockW.toFixed(2)} × {der.blockH.toFixed(2)} m
            </strong>
          </div>
          <div>
            Run length: {der.runLen.toFixed(2)}m ({der.steps} steps ×{" "}
            {der.depth}m)
          </div>
          <div>
            Climbs UP toward: <strong>{der.upDir.toUpperCase()}</strong>
          </div>
        </div>
      </Section>

      {/* ── Openings section (doors + open doors only — no windows) ── */}
      <Section>
        <H4>Doors and open doors</H4>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>
          Doors default to <strong>MAIN</strong> — leaf swings OUTSIDE the
          stairs. Uncheck "Main door" for a regular door.
        </div>

        {(d.doors || []).map((o, i) => (
          <div key={`d${i}`} style={{ fontSize: 11, marginBottom: 3 }}>
            <Chip $type="door">{o.is_main ? "MAIN" : "DOOR"}</Chip>
            {wallLabel(o.wall)} pos {o.position.toFixed(2)}m w {o.width}m
            <DeleteBtn
              onClick={() => upd({ doors: d.doors.filter((_, j) => j !== i) })}
            >
              ×
            </DeleteBtn>
          </div>
        ))}
        {(d.open_doors || []).map((o, i) => (
          <div key={`o${i}`} style={{ fontSize: 11, marginBottom: 3 }}>
            <Chip $type="open">OPEN</Chip>
            {wallLabel(o.wall)} pos {o.position.toFixed(2)}m w{" "}
            {o.width.toFixed(2)}m
            <DeleteBtn
              onClick={() =>
                upd({ open_doors: d.open_doors.filter((_, j) => j !== i) })
              }
            >
              ×
            </DeleteBtn>
          </div>
        ))}

        {!openingMode && (
          <Row style={{ marginTop: 8, marginBottom: 0 }}>
            <SmallBtn $primary onClick={() => startOpening("door")}>
              + Door
            </SmallBtn>
            <SmallBtn $primary onClick={() => startOpening("open_door")}>
              + Open door
            </SmallBtn>
          </Row>
        )}

        {openingMode && !openingDraft?.wall && (
          <div
            style={{
              marginTop: 8,
              padding: 8,
              background: "#fff3cd",
              border: "1px solid #ffeeba",
              borderRadius: 4,
              fontSize: 12,
            }}
          >
            Click on a wall of the stairs to place the{" "}
            <strong>{openingMode.replace("_", " ")}</strong>.
            <SmallBtn onClick={cancelOpening} style={{ float: "right" }}>
              Cancel
            </SmallBtn>
          </div>
        )}

        {openingMode === "door" && openingDraft?.wall && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, marginBottom: 6 }}>
              Wall: <strong>{wallLabel(openingDraft.wall)}</strong>, hinge at{" "}
              {openingDraft.pos.toFixed(2)}m
            </div>
            <Label>Width (m) — range 0.7 to 2.0</Label>
            <Input
              type="number"
              min="0.7"
              max="2.0"
              step={inputStep}
              value={openingDraft.width || 1.1}
              onChange={(e) => {
                let w = parseFloat(e.target.value) || 0;
                if (w > 0 && w < 0.7) w = 0.7;
                if (w > 2.0) w = 2.0;
                setOpeningDraft({ ...openingDraft, width: w });
              }}
            />

            <label
              style={{
                display: "block",
                marginTop: 8,
                fontSize: 12,
                padding: 6,
                background: "#eef7f0",
                border: "1px solid #bfdac9",
                borderRadius: 4,
              }}
            >
              <input
                type="checkbox"
                checked={openingDraft.is_main !== false}
                onChange={(e) => {
                  const isMain = e.target.checked;
                  let patch = { ...openingDraft, is_main: isMain };
                  if (openingDraft.rotation) {
                    patch.swing = swingFromRotation(
                      openingDraft.wall,
                      openingDraft.rotation,
                      isMain
                    );
                    patch.hinge_side = hingeFromRotation(
                      openingDraft.wall,
                      openingDraft.rotation,
                      isMain
                    );
                  }
                  setOpeningDraft(patch);
                }}
              />{" "}
              <strong>Main door</strong> — leaf swings OUTSIDE the stairs
            </label>

            <Label style={{ marginTop: 8 }}>Rotation</Label>
            <Row style={{ marginBottom: 6 }}>
              <label style={{ flex: 1, fontSize: 12 }}>
                <input
                  type="radio"
                  checked={openingDraft.rotation === "clockwise"}
                  onChange={() => {
                    const isMain = openingDraft.is_main !== false;
                    const swing = swingFromRotation(
                      openingDraft.wall,
                      "clockwise",
                      isMain
                    );
                    const hinge = hingeFromRotation(
                      openingDraft.wall,
                      "clockwise",
                      isMain
                    );
                    setOpeningDraft({
                      ...openingDraft,
                      rotation: "clockwise",
                      swing,
                      hinge_side: hinge,
                    });
                  }}
                />{" "}
                CW
              </label>
              <label style={{ flex: 1, fontSize: 12 }}>
                <input
                  type="radio"
                  checked={openingDraft.rotation === "counterclockwise"}
                  onChange={() => {
                    const isMain = openingDraft.is_main !== false;
                    const swing = swingFromRotation(
                      openingDraft.wall,
                      "counterclockwise",
                      isMain
                    );
                    const hinge = hingeFromRotation(
                      openingDraft.wall,
                      "counterclockwise",
                      isMain
                    );
                    setOpeningDraft({
                      ...openingDraft,
                      rotation: "counterclockwise",
                      swing,
                      hinge_side: hinge,
                    });
                  }}
                />{" "}
                CCW
              </label>
            </Row>
            <Row style={{ marginTop: 8 }}>
              <SmallBtn
                $primary
                onClick={commitDoor}
                disabled={!openingDraft.rotation}
              >
                {openingDraft.is_main !== false ? "Add main door" : "Add door"}
              </SmallBtn>
              <SmallBtn onClick={cancelOpening}>Cancel</SmallBtn>
            </Row>
          </div>
        )}

        {openingMode === "open_door" && openingDraft && (
          <div style={{ marginTop: 8, fontSize: 12 }}>
            {openingDraft.pos1 == null && (
              <>
                Click <strong>begin point</strong> on any wall.
              </>
            )}
            {openingDraft.pos1 != null && openingDraft.pos2 == null && (
              <>
                Begin: {wallLabel(openingDraft.wall)} @{" "}
                {openingDraft.pos1.toFixed(2)}m. Now click{" "}
                <strong>end point</strong> on the same wall.
              </>
            )}
            {openingDraft.pos2 != null && (
              <div>
                {wallLabel(openingDraft.wall)}: {openingDraft.pos1.toFixed(2)}m
                → {openingDraft.pos2.toFixed(2)}m (width{" "}
                {Math.abs(openingDraft.pos2 - openingDraft.pos1).toFixed(2)}m)
                <Row style={{ marginTop: 6 }}>
                  <SmallBtn $primary onClick={commitOpenDoor}>
                    Add open door
                  </SmallBtn>
                  <SmallBtn onClick={cancelOpening}>Cancel</SmallBtn>
                </Row>
              </div>
            )}
          </div>
        )}
      </Section>

      {isEditing ? (
        <>
          <AddBtn
            disabled={!canAdd}
            onClick={onCommitElement}
            style={{ background: canAdd ? "#2e8b57" : "#ccc" }}
          >
            {canAdd
              ? "Save changes"
              : !d.name
                ? "Fill name to save"
                : "Width ≥ 2m, steps ≥ 2, depth ≥ 0.3m"}
          </AddBtn>
          <button
            onClick={onCancelEdit}
            style={{
              width: "100%",
              marginTop: 6,
              padding: "8px 10px",
              background: "#f4f4f4",
              color: "#333",
              border: "1px solid #ccc",
              borderRadius: 6,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Cancel edit
          </button>
        </>
      ) : (
        <AddBtn disabled={!canAdd} onClick={onCommitElement}>
          {canAdd
            ? "Add stairs to plan"
            : !d.name
              ? "Fill name to add"
              : "Width ≥ 2m, steps ≥ 2, depth ≥ 0.3m"}
        </AddBtn>
      )}
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  STANDALONE WALL FORM  (Stage 3j)
// ═════════════════════════════════════════════════════════════════════════
//
// Two endpoints only. No openings. Python renders as double-line 0.2m
// thick with auto-hidden caps where endpoints touch existing walls.
// - Min length: 0.5 m (avoids accidental tiny walls from double-clicks).
// - Snap while placing points: existing polygon points → axis-aligned →
//   grid (same as CUSTOM polygon snap, via snapCustomPoint).

function WallForm({
  drawing,
  setDrawing,
  editIndex,
  onCommitElement,
  onCancelEdit,
  inputStep = 0.1,
  setInputStep,
}) {
  const d = drawing;
  const upd = (patch) => setDrawing({ ...d, ...patch });

  const length = Math.hypot(d.x2 - d.x1, d.y2 - d.y1);
  const bothPlaced = d.wallPhase === "done";
  const canAdd = d.name && bothPlaced && length >= 0.5;

  const isEditing = editIndex !== null;

  return (
    <>
      <div
        style={{
          padding: 8,
          background: isEditing ? "#fff8dc" : "#e0f2f1",
          border: isEditing ? "1px solid #f0c040" : "1px solid #b2dfdb",
          borderRadius: 6,
          marginBottom: 12,
          fontSize: 13,
        }}
      >
        {isEditing ? (
          <>
            Editing wall: <strong>{d.label || d.name}</strong>
          </>
        ) : (
          <>
            Drawing: <strong>BEAM</strong> (2 endpoints)
          </>
        )}
      </div>

      <Row>
        <div style={{ flex: 1 }}>
          <Label>Name</Label>
          <Input
            value={d.name}
            onChange={(e) => upd({ name: e.target.value })}
            placeholder="e.g. wall1"
          />
        </div>
        <div style={{ flex: 1 }}>
          <Label>Label (blank OK)</Label>
          <Input
            value={d.label}
            onChange={(e) => upd({ label: e.target.value })}
            placeholder="e.g. partition"
          />
        </div>
      </Row>

      <Section>
        <H4>Endpoints — click on the canvas or type below</H4>

        {!isEditing && d.wallPhase === "p1" && (
          <div
            style={{
              padding: 6,
              background: "#fff3cd",
              border: "1px solid #ffeeba",
              borderRadius: 4,
              fontSize: 12,
              marginBottom: 8,
            }}
          >
            Click <strong>point 1</strong> on the canvas.
          </div>
        )}
        {!isEditing && d.wallPhase === "p2" && (
          <div
            style={{
              padding: 6,
              background: "#fff3cd",
              border: "1px solid #ffeeba",
              borderRadius: 4,
              fontSize: 12,
              marginBottom: 8,
            }}
          >
            Click <strong>point 2</strong> on the canvas.
          </div>
        )}

        <Row style={{ marginBottom: 10, alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "#555" }}>Step:</span>
          <button
            type="button"
            onClick={() => setInputStep && setInputStep(0.1)}
            style={{
              padding: "3px 10px",
              fontSize: 12,
              background: inputStep === 0.1 ? "#2e8b57" : "#fff",
              color: inputStep === 0.1 ? "white" : "#333",
              border: "1px solid #bfdac9",
              borderRadius: 4,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            0.1 m
          </button>
          <button
            type="button"
            onClick={() => setInputStep && setInputStep(0.01)}
            style={{
              padding: "3px 10px",
              fontSize: 12,
              background: inputStep === 0.01 ? "#2e8b57" : "#fff",
              color: inputStep === 0.01 ? "white" : "#333",
              border: "1px solid #bfdac9",
              borderRadius: 4,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            0.01 m
          </button>
        </Row>

        <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
          Point 1
        </div>
        <Row>
          <div style={{ flex: 1 }}>
            <Label>x₁ (m)</Label>
            <Input
              type="number"
              step={inputStep}
              value={d.x1}
              onChange={(e) => upd({ x1: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label>y₁ (m)</Label>
            <Input
              type="number"
              step={inputStep}
              value={d.y1}
              onChange={(e) => upd({ y1: parseFloat(e.target.value) || 0 })}
            />
          </div>
        </Row>

        <div
          style={{ fontSize: 11, color: "#666", marginBottom: 4, marginTop: 6 }}
        >
          Point 2
        </div>
        <Row>
          <div style={{ flex: 1 }}>
            <Label>x₂ (m)</Label>
            <Input
              type="number"
              step={inputStep}
              value={d.x2}
              onChange={(e) =>
                upd({
                  x2: parseFloat(e.target.value) || 0,
                  // If user types point 2 before clicking, jump to done.
                  wallPhase: d.wallPhase === "p1" ? "p2" : "done",
                })
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label>y₂ (m)</Label>
            <Input
              type="number"
              step={inputStep}
              value={d.y2}
              onChange={(e) =>
                upd({
                  y2: parseFloat(e.target.value) || 0,
                  wallPhase: d.wallPhase === "p1" ? "p2" : "done",
                })
              }
            />
          </div>
        </Row>

        {(bothPlaced || isEditing) && (
          <div
            style={{
              marginTop: 8,
              padding: 6,
              background: "#eef7f0",
              border: "1px solid #bfdac9",
              borderRadius: 4,
              fontSize: 12,
            }}
          >
            Length: <strong>{length.toFixed(2)} m</strong>
            {length < 0.5 && (
              <span style={{ color: "#c0392b", marginLeft: 8 }}>
                (minimum 0.5 m)
              </span>
            )}
          </div>
        )}

        {!isEditing && bothPlaced && (
          <SmallBtn
            style={{ marginTop: 8 }}
            onClick={() =>
              upd({
                wallPhase: "p1",
                x1: 0,
                y1: 0,
                x2: 0,
                y2: 0,
              })
            }
          >
            Redo endpoints
          </SmallBtn>
        )}
      </Section>

      {isEditing ? (
        <>
          <AddBtn
            disabled={!canAdd}
            onClick={onCommitElement}
            style={{ background: canAdd ? "#2e8b57" : "#ccc" }}
          >
            {canAdd
              ? "Save changes"
              : !d.name
                ? "Fill name to save"
                : length < 0.5
                  ? "Length must be ≥ 0.5 m"
                  : "Place both endpoints"}
          </AddBtn>
          <button
            onClick={onCancelEdit}
            style={{
              width: "100%",
              marginTop: 6,
              padding: "8px 10px",
              background: "#f4f4f4",
              color: "#333",
              border: "1px solid #ccc",
              borderRadius: 6,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Cancel edit
          </button>
        </>
      ) : (
        <AddBtn disabled={!canAdd} onClick={onCommitElement}>
          {canAdd
            ? "Add wall to plan"
            : !d.name
              ? "Fill name to add"
              : !bothPlaced
                ? "Place both endpoints"
                : "Length must be ≥ 0.5 m"}
        </AddBtn>
      )}
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────
//  COLUMN FORM  (Stage 5)
//  One click places the column centered at the point. Discrete dropdowns
//  for thickness and width; direction x / y / angled; angle field shown
//  only when direction === "angled".
// ─────────────────────────────────────────────────────────────────────
const COLUMN_THICKNESSES = [0.2, 0.25, 0.3];
const COLUMN_WIDTHS = [0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0];

function ColumnForm({
  drawing,
  setDrawing,
  editIndex,
  onCommitElement,
  onCancelEdit,
  inputStep = 0.1,
  setInputStep,
}) {
  const d = drawing;
  const upd = (patch) => setDrawing({ ...d, ...patch });

  const placed = d.columnPhase === "done" || editIndex !== null;
  const canAdd = d.name && placed;
  const isEditing = editIndex !== null;

  const selStyle = {
    width: "100%",
    padding: "4px 6px",
    fontSize: 13,
    border: "1px solid #ccc",
    borderRadius: 4,
  };

  return (
    <>
      <div
        style={{
          padding: 8,
          background: isEditing ? "#fff8dc" : "#fdecea",
          border: isEditing ? "1px solid #f0c040" : "1px solid #f5b5ae",
          borderRadius: 6,
          marginBottom: 12,
          fontSize: 13,
        }}
      >
        {isEditing ? (
          <>Editing column: <strong>{d.label || d.name}</strong></>
        ) : (
          <>Drawing: <strong>COLUMN</strong> (click to place)</>
        )}
      </div>

      <Row>
        <Label>Name</Label>
        <Input
          type="text"
          value={d.name}
          onChange={(e) => upd({ name: e.target.value })}
          placeholder="e.g. C1"
        />
      </Row>

      <Row>
        <Label>Label</Label>
        <Input
          type="text"
          value={d.label}
          onChange={(e) => upd({ label: e.target.value })}
          placeholder="(optional)"
        />
      </Row>

      {/* Step toggle for the x / y inputs */}
      <Row style={{ alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 12, color: "#555" }}>Step:</span>
        <button
          type="button"
          onClick={() => setInputStep && setInputStep(0.1)}
          style={{
            padding: "3px 10px", fontSize: 12,
            background: inputStep === 0.1 ? "#2e8b57" : "#fff",
            color: inputStep === 0.1 ? "white" : "#333",
            border: "1px solid #bfdac9", borderRadius: 4,
            cursor: "pointer", fontWeight: 600,
          }}
        >0.1 m</button>
        <button
          type="button"
          onClick={() => setInputStep && setInputStep(0.01)}
          style={{
            padding: "3px 10px", fontSize: 12,
            background: inputStep === 0.01 ? "#2e8b57" : "#fff",
            color: inputStep === 0.01 ? "white" : "#333",
            border: "1px solid #bfdac9", borderRadius: 4,
            cursor: "pointer", fontWeight: 600,
          }}
        >0.01 m</button>
      </Row>

      <Row>
        <Label>Center X (m)</Label>
        <Input
          type="number"
          step={inputStep}
          value={d.x}
          onChange={(e) => upd({ x: parseFloat(e.target.value) || 0 })}
        />
      </Row>

      <Row>
        <Label>Center Y (m)</Label>
        <Input
          type="number"
          step={inputStep}
          value={d.y}
          onChange={(e) => upd({ y: parseFloat(e.target.value) || 0 })}
        />
      </Row>

      <Row>
        <Label>Thickness (m)</Label>
        <select
          style={selStyle}
          value={d.thickness}
          onChange={(e) => upd({ thickness: parseFloat(e.target.value) })}
        >
          {COLUMN_THICKNESSES.map((t) => (
            <option key={t} value={t}>{t.toFixed(2)}</option>
          ))}
        </select>
      </Row>

      <Row>
        <Label>Width (m)</Label>
        <select
          style={selStyle}
          value={d.width}
          onChange={(e) => upd({ width: parseFloat(e.target.value) })}
        >
          {COLUMN_WIDTHS.map((w) => (
            <option key={w} value={w}>{w.toFixed(2)}</option>
          ))}
        </select>
      </Row>

      <Row>
        <Label>Direction</Label>
        <select
          style={selStyle}
          value={d.direction}
          onChange={(e) => upd({ direction: e.target.value })}
        >
          <option value="x">X (width along X)</option>
          <option value="y">Y (width along Y)</option>
          <option value="angled">Angled</option>
        </select>
      </Row>

      {d.direction === "angled" && (
        <Row>
          <Label>Angle (°)</Label>
          <Input
            type="number"
            step="1"
            value={d.angle}
            onChange={(e) => upd({ angle: parseFloat(e.target.value) || 0 })}
          />
        </Row>
      )}

      {isEditing ? (
        <>
          <AddBtn disabled={!canAdd} onClick={onCommitElement}>
            {canAdd ? "Save changes" : "Fill name"}
          </AddBtn>
          <button
            onClick={onCancelEdit}
            style={{
              marginTop: 8, width: "100%", padding: "6px",
              background: "#eee", border: "1px solid #ccc",
              borderRadius: 4, cursor: "pointer",
            }}
          >
            Cancel edit
          </button>
        </>
      ) : (
        <AddBtn disabled={!canAdd} onClick={onCommitElement}>
          {canAdd
            ? "Add column to plan"
            : !d.name
              ? "Fill name to add"
              : "Click on the canvas to place"}
        </AddBtn>
      )}
    </>
  );
}
